import React, { useState, useMemo, useCallback, useRef, useEffect, useDeferredValue } from "react";
import { useData, USER_TABLE, ADMIN_TABLE } from "../context/DataContext";
import { cellText, getStatusBadge, getPriorityBadge, formatDate, formatDateTime, normalizeToMs, userMatchesField, buildActiveUserIndex, buildDeletedUserIndex, deletedAtFor } from "../utils/helpers";
import EditTaskModal from "./EditTaskModal";
import DeleteConfirmModal from "./DeleteConfirmModal";
import BulkAssigneeRemoveModal from "./BulkAssigneeRemoveModal";
import CommentPanel from "./CommentPanel";
import CustomSelect from "./CustomSelect";
import { useAuth } from "../context/AuthContext";
import { exportTasksToExcel } from "../utils/exportTasks";

const KEY_COLS = [
  "Task ID", "Task Name", "Overall Status", "Priority",
  "Assigned Users", "Assigned By", "Supervisor",
  "Start Date", "End Date", "Company Name",
  "Assignee Department", "Creator Department",
  "Recurring Type", "Task Description",
];

function ScrollSentinel({ onVisible }) {
  const ref = useRef(null);
  const cbRef = useRef(onVisible);
  cbRef.current = onVisible;
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) cbRef.current();
    }, { rootMargin: "200px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return <div ref={ref} style={{ height: 1 }} />;
}

export default function TaskTable({
  records,
  tableId,
  emptyMsg = "No tasks found",
  canEdit = false,
  canDelete = false,
  canComplete = false,
  statusOptionsOverride = null,
  hideStatusFilter = false,
  title = "",
  sortDueAsc = false,
  kanbanLayout = "3col",
}) {
  const [search, setSearch] = useState("");
  // The input updates instantly; the expensive re-filter/re-render trails it.
  // Without this, every keystroke blocked on filtering + repainting every card.
  const deferredSearch = useDeferredValue(search);
  const [statusFilter, setStatusFilter] = useState([]);
  const [priFilter, setPriFilter] = useState([]);
  const [employeeFilter, setEmployeeFilter] = useState([]);
  const [creatorFilter, setCreatorFilter] = useState([]);
  const [companyFilter, setCompanyFilter] = useState([]);
  const [assigneeDeptFilter, setAssigneeDeptFilter] = useState([]);
  const [creatorDeptFilter, setCreatorDeptFilter] = useState([]);
  const [timeFilter, setTimeFilter] = useState("all");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [sortCol, setSortCol] = useState(null);
  const [sortAsc, setSortAsc] = useState(true);
  const [view, setView] = useState("kanban"); // "kanban" | "grid" | "gantt"
  const CARDS_PER_PAGE = 50;
  const [colLimits, setColLimits] = useState({});
  const getColLimit = (key) => colLimits[key] || CARDS_PER_PAGE;
  const showMoreCol = (key) => setColLimits(prev => ({ ...prev, [key]: (prev[key] || CARDS_PER_PAGE) + CARDS_PER_PAGE }));
  const [gridLimit, setGridLimit] = useState(100);
  const [exporting, setExporting] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const statusOptions = useMemo(() => {
    if (statusOptionsOverride) return statusOptionsOverride;
    const opts = new Set();
    (records || []).forEach(r => {
      const s = cellText(r.fields?.["Overall Status"]) || "Not Started";
      opts.add(s);
    });
    return Array.from(opts).sort((a, b) => {
      // Keep "Done" related items at the bottom of the list for better UX
      const ad = a.toLowerCase().includes("done");
      const bd = b.toLowerCase().includes("done");
      if (ad && !bd) return 1;
      if (!ad && bd) return -1;
      return a.localeCompare(b);
    });
  }, [records, statusOptionsOverride]);

  const companyOptions = useMemo(() => {
    const opts = new Set(["Aquint Infratele India Pvt Ltd", "Arkonik Fibretech India Pvt Ltd", "Aquint Global Techskill India Pvt Ltd"]);
    (records || []).forEach(r => {
      const val = cellText(r.fields?.["Company Name"]);
      if (val && val !== "—") {
        val.split(",").map(s => s.trim()).forEach(s => {
          if (s) opts.add(s);
        });
      }
    });
    return Array.from(opts).sort();
  }, [records]);

  const assigneeDeptOptions = useMemo(() => {
    const opts = new Set(["HR & ADMIN", "ACCOUNT & FINANCE", "PROJECT EXECUTION", "PURCHASE", "TENDER & MARKETING"]);
    (records || []).forEach(r => {
      const val = cellText(r.fields?.["Assignee Department"]);
      if (val && val !== "—" && val.trim()) opts.add(val.trim());
    });
    return Array.from(opts).sort();
  }, [records]);

  const creatorDeptOptions = useMemo(() => {
    const opts = new Set(["HR & ADMIN", "ACCOUNT & FINANCE", "PROJECT EXECUTION", "PURCHASE", "TENDER & MARKETING"]);
    (records || []).forEach(r => {
      const val = cellText(r.fields?.["Creator Department"]);
      if (val && val !== "—" && val.trim()) opts.add(val.trim());
    });
    return Array.from(opts).sort();
  }, [records]);


  const [editRecord, setEditRecord] = useState(null);
  const [deleteRecord, setDeleteRecord] = useState(null);
  const [completing, setCompleting] = useState({});
  const [reopening, setReopening] = useState({});
  const [expanded, setExpanded] = useState({});
  const [commentTask, setCommentTask] = useState(null);
  const [flashTaskId, setFlashTaskId] = useState(null);
  const [taskAttachments, setTaskAttachments] = useState({});
  const [pointsPanel, setPointsPanel] = useState(null); // taskId of open panel
  const [newPointText, setNewPointText] = useState({}); // { [taskId]: "text" }
  const [editingPoint, setEditingPoint] = useState(null); // { taskId, pointId, text }
  const [pointSaving, setPointSaving] = useState(false);
  const [toastMsg, setToastMsg] = useState("");
  const loadedRef = useRef({});

  const showToast = (msg) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(""), 3000);
  };

  // Ref to hold the latest kanbanBuckets so the notification handler can
  // expand the right column without depending on the useMemo directly.
  const kanbanBucketsRef = useRef({});
  // Pending flash: stored when notification opens the chat; triggered after chat closes.
  const pendingFlashRef = useRef(null);

  // When comment panel closes and there's a pending notification flash,
  // trigger the glow + scroll on the card.
  React.useEffect(() => {
    if (commentTask === null && pendingFlashRef.current) {
      const targetId = pendingFlashRef.current;
      pendingFlashRef.current = null;

      // Expand the column containing the task
      const buckets = kanbanBucketsRef.current || {};
      for (const key of Object.keys(buckets)) {
        const items = buckets[key] || [];
        if (items.some(r => String(r.fields?.["Task ID"] || "") === targetId)) {
          setColExpanded(p => ({ ...p, [key]: true }));
          break;
        }
      }

      // Small delay for column expand animation, then glow + scroll
      setTimeout(() => {
        setFlashTaskId(targetId);
        const el = document.getElementById(`kanban-card-${targetId}`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
        setTimeout(() => setFlashTaskId(curr => (curr === targetId ? null : curr)), 3000);
      }, 300);
    }
  }, [commentTask]);

  // Listen for "open task's comments" requests from the notification bell.
  React.useEffect(() => {
    const handler = (e) => {
      const targetId = String(e.detail?.taskId || "");
      if (!targetId) return;
      const found = (records || []).find(r => String(r.fields?.["Task ID"] || "") === targetId);
      if (!found) {
        // Task not on this page — tell the app to try monitor
        window.dispatchEvent(new CustomEvent("tms:task-not-found", { detail: { taskId: targetId } }));
        return;
      }

      // Store the flash for after chat closes
      pendingFlashRef.current = targetId;
      setCommentTask(found);
    };
    window.addEventListener("tms:open-task-comments", handler);
    return () => window.removeEventListener("tms:open-task-comments", handler);
  }, [records]);

  const { user } = useAuth();
  const { updateTask, deleteTask, refresh, getAttachments, deleteAttachment, uploadFile, larkUsers, deletedUsers, commentCounts, readComments, userRecords, adminRecords: allAdminRecords, reopenUserTask, allPoints, setAllPoints, addPoint, updatePoint, deletePoint } = useData();
  const isAdmin = user?.role === "admin";

  const employeeOptions = useMemo(() => {
    const names = new Set();
    (records || []).forEach(r => {
      const f = r.fields || {};
      // Gather from Assigned Users
      const assignedRaw = f["Total Assigned Users"] || f["All Assigned Users"] || f["Assigned Users"] || f["User"];
      if (Array.isArray(assignedRaw)) {
        assignedRaw.forEach(v => {
          const n = v?.en_name || v?.name;
          if (n) names.add(n);
        });
      } else {
        const rawStr = cellText(assignedRaw);
        if (rawStr && rawStr !== "—") rawStr.split(",").map(s => s.trim()).filter(Boolean).forEach(n => names.add(n));
      }
    });
    // Deleted users are KEPT here on purpose: their old tasks still exist, so you
    // need to be able to filter by them. They are only excluded from the pickers
    // that assign NEW work (those read the active-only `larkUsers` roster).
    //
    // Emitted as { id, name }: CustomSelect matches on `id` and displays `name`,
    // so the filter still compares the raw assignee name while the dropdown can
    // show a "(left)" marker. Appending to the plain string would break matching.
    const activeIndex = buildActiveUserIndex(larkUsers);
    return Array.from(names)
      .sort((a, b) => a.localeCompare(b))
      .map(n => {
        // activeIndex is null until the roster loads — mark nobody until then.
        const gone = activeIndex && !activeIndex.names.has(n.trim().toLowerCase());
        return gone ? { id: n, name: `${n} (left)` } : n;
      });
  }, [records, larkUsers]);

  // ── Search index ────────────────────────────────────────
  // KEY_COLS were being re-stringified for every record on every keystroke
  // (14 cellText + toLowerCase calls per row). Build the haystack once per
  // records change instead; searching then costs one substring test per row.
  // Built lazily on the first keystroke rather than eagerly on every page load,
  // then cached until `records` changes — pages nobody searches pay nothing.
  const blobCacheRef = useRef({ src: null, map: null });
  const getSearchBlobs = useCallback(() => {
    if (blobCacheRef.current.src === records && blobCacheRef.current.map) {
      return blobCacheRef.current.map;
    }
    const m = new Map();
    for (const r of (records || [])) {
      const f = r.fields || {};
      let blob = "";
      for (const k of KEY_COLS) {
        const v = cellText(f[k]);
        if (v) blob += v + " ";
      }
      m.set(r, blob.toLowerCase());
    }
    blobCacheRef.current = { src: records, map: m };
    return m;
  }, [records]);

  // ── Search suggestions ──────────────────────────────────
  // Typeahead built from the records already on the page — no API call. Three
  // kinds of hit: a specific task, an assignee, or a company. Picking one just
  // fills the box, so the existing `filtered` search keeps doing the work.
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchHighlight, setSearchHighlight] = useState(-1);
  const searchBoxRef = useRef(null);

  const searchSuggestions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    const tasks = [], seenTask = new Set();
    const companies = new Set();
    const blobs = getSearchBlobs();

    for (const r of (records || [])) {
      // Everything we could display is already collected — stop scanning.
      // Without this the whole list was walked on every keystroke.
      if (tasks.length >= 8 && companies.size >= 3) break;
      // Cheap pre-filter: the prebuilt haystack rules a row out in one test,
      // so the per-field work below only runs for rows that can actually match.
      const blob = blobs.get(r);
      if (blob !== undefined && !blob.includes(q)) continue;

      const f = r.fields || {};
      const tName = cellText(f["Task Name"]);
      const tId = cellText(f["Task ID"]);
      if (tasks.length < 8 &&
          ((tName && tName.toLowerCase().includes(q)) || (tId && tId.toLowerCase().includes(q)))) {
        const key = tId || tName;
        if (key && !seenTask.has(key)) {
          seenTask.add(key);
          tasks.push({ kind: "task", value: tName || tId, label: tName || "Untitled", meta: tId });
        }
      }

      cellText(f["Company Name"]).split(",").forEach(c => {
        const t = c.trim();
        if (t && t !== "—" && t.toLowerCase().includes(q)) companies.add(t);
      });
    }

    // Employees are deliberately NOT suggested here — the "All Employees"
    // dropdown already covers picking a person. Searching by name still WORKS:
    // KEY_COLS includes Assigned Users / Assigned By / Supervisor, so typing a
    // name still returns that person's tasks. Only the suggestion list changed.
    return [
      ...tasks.slice(0, 8),
      ...Array.from(companies).sort().slice(0, 3).map(c => ({ kind: "company", value: c, label: c })),
    ];
  }, [records, search, getSearchBlobs]);

  const applySuggestion = useCallback((sug) => {
    if (!sug) return;
    setSearch(sug.kind === "task" && sug.meta ? sug.meta : sug.value);
    setSearchOpen(false);
    setSearchHighlight(-1);
  }, []);

  const onSearchKeyDown = useCallback((e) => {
    if (!searchOpen || searchSuggestions.length === 0) {
      if (e.key === "Escape") { setSearch(""); setSearchOpen(false); }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSearchHighlight(i => (i + 1) % searchSuggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSearchHighlight(i => (i <= 0 ? searchSuggestions.length - 1 : i - 1));
    } else if (e.key === "Enter") {
      if (searchHighlight >= 0) { e.preventDefault(); applySuggestion(searchSuggestions[searchHighlight]); }
      else setSearchOpen(false);
    } else if (e.key === "Escape") {
      setSearchOpen(false);
      setSearchHighlight(-1);
    }
  }, [searchOpen, searchSuggestions, searchHighlight, applySuggestion]);

  // Close the dropdown when focus moves elsewhere on the page.
  useEffect(() => {
    if (!searchOpen) return;
    const onDocDown = (e) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(e.target)) setSearchOpen(false);
    };
    document.addEventListener("mousedown", onDocDown);
    return () => document.removeEventListener("mousedown", onDocDown);
  }, [searchOpen]);

  // ── Deleted-assignee detection ──────────────────────────
  // A task still pointing at someone deleted in Lark is a problem its CREATOR
  // has to fix: nothing new should be assigned to a departed employee, and a
  // recurring one keeps generating work for nobody until it is reassigned or
  // removed. Flag it wherever the card appears, including "Tasks I Created".
  const deletedIndex = useMemo(() => buildDeletedUserIndex(deletedUsers), [deletedUsers]);
  const activeIndex = useMemo(() => buildActiveUserIndex(larkUsers), [larkUsers]);

  const getDeletedAssignees = useCallback((fields) => {
    // Until the roster loads we know nothing, so flag nobody.
    if (!activeIndex) return [];
    const raw = fields?.["Total Assigned Users"] || fields?.["All Assigned Users"] || fields?.["Assigned Users"] || fields?.["User"];
    if (!Array.isArray(raw)) return [];
    const out = [];
    for (const v of raw) {
      if (!v) continue;
      const id = v.id || v.open_id || "";
      const name = v.en_name || v.name || "";
      // Only trust an explicit id — a bare name we cannot resolve is not
      // evidence of deletion, and false positives here would be alarming.
      if (!id || activeIndex.ids.has(String(id))) continue;
      const goneAt = deletedAtFor(deletedIndex, { id, name });
      out.push({ id, name: name || "Unknown", deletedAt: goneAt });
    }
    return out;
  }, [activeIndex, deletedIndex]);


  // Build a lookup: taskId → Task Creator field from admin records
  // so we can check creator even on USER_TABLE cards
  const adminCreatorMap = useMemo(() => {
    const m = {};
    for (const ar of (allAdminRecords || [])) {
      const tid = cellText(ar.fields?.["Task ID"]);
      if (!tid || tid === "—") continue;
      m[tid] = ar.fields?.["Task Creator"] || ar.fields?.["Assigned By"];
    }
    return m;
  }, [allAdminRecords]);

  // Who created the tasks on this page. A USER_TABLE row has no creator of its
  // own, so fall back through adminCreatorMap exactly as the cards do.
  const creatorOptions = useMemo(() => {
    const names = new Set();
    for (const r of (records || [])) {
      const f = r.fields || {};
      const tid = cellText(f["Task ID"]);
      const raw = (tid && adminCreatorMap[tid]) || f["Task Creator"] || f["Assigned By"];
      if (Array.isArray(raw)) {
        raw.forEach(v => { const n = v?.en_name || v?.name; if (n) names.add(n); });
      } else {
        const str = cellText(raw);
        if (str && str !== "—") str.split(",").map(x => x.trim()).filter(Boolean).forEach(n => names.add(n));
      }
    }
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [records, adminCreatorMap]);


  // Build lookup: taskId → full admin record (for editing user-table rows via admin record)
  const adminRecordByTaskId = useMemo(() => {
    const m = {};
    for (const ar of (allAdminRecords || [])) {
      const tid = cellText(ar.fields?.["Task ID"]);
      if (!tid || tid === "—") continue;
      m[tid] = ar;
    }
    return m;
  }, [allAdminRecords]);

  // Fallback: latest per-user "Completed At" per Task ID, used when the admin
  // record's own Completed At/Completion Time is empty (Lark sometimes only
  // fills it for late completions).
  const userCompletionByTaskId = useMemo(() => {
    const map = {};
    for (const ur of (userRecords || [])) {
      const uf = ur.fields || {};
      if (!uf["My Completion"]) continue;
      const tid = cellText(uf["Task ID"]);
      if (!tid || tid === "—") continue;
      const ms = normalizeToMs(uf["Completed At"]);
      if (ms > 0 && ms > (map[tid] || 0)) map[tid] = ms;
    }
    return map;
  }, [userRecords]);

  const loadAttachments = useCallback(async (taskId) => {
    if (!taskId || loadedRef.current[taskId]) return;
    loadedRef.current[taskId] = true;
    setTaskAttachments(p => ({ ...p, [taskId]: [] }));
    try {
      const data = await getAttachments(taskId);
      setTaskAttachments(p => ({ ...p, [taskId]: data }));
    } catch (e) { console.error(e); }
  }, [getAttachments]);

  const handleAttachFile = async (taskId, file) => {
    if (!file || !taskId) return;
    try {
      await uploadFile(file, String(taskId));
      const data = await getAttachments(taskId);
      setTaskAttachments(p => ({ ...p, [taskId]: data }));
    } catch (e) {
      alert("Attachment failed: " + e.message);
    }
  };
  const showActions = canEdit || canDelete || canComplete;

  const handleComplete = useCallback(async (row) => {
    const rid = row.record_id;
    const newVal = !row.fields?.["My Completion"];
    setCompleting(p => ({ ...p, [rid]: true }));
    try {
      // Only send "My Completion" — worker will set "Completed By" on ADMIN_TABLE using JWT
      const updateFields = { "My Completion": newVal };
      await updateTask(tableId, rid, updateFields);
      showToast(newVal ? "✅ Task marked as done!" : "↺ Task reopened");
    } catch (e) {
      alert("Failed: " + e.message);
    } finally {
      setCompleting(p => ({ ...p, [rid]: false }));
    }
  }, [updateTask, tableId, user?.userId]);

  const handleReopenUser = useCallback(async (taskId, userId, userName) => {
    const key = `${taskId}-${userId}`;
    setReopening(p => ({ ...p, [key]: true }));
    try {
      await reopenUserTask(taskId, userId);
      showToast(`Reopened task for ${userName}`);
      refresh(true);
    } catch (e) {
      alert("Failed to reopen: " + e.message);
    } finally {
      setReopening(p => ({ ...p, [key]: false }));
    }
  }, [reopenUserTask, refresh]);

  const handleAddPoint = useCallback(async (taskId, text) => {
    if (!text.trim() || !taskId) return;
    setPointSaving(true);
    try {
      const data = await addPoint(taskId, text.trim());
      setAllPoints(p => ({
        ...p,
        [taskId]: Array.isArray(data.points) ? data.points : [...(p[taskId] || []), data.point]
      }));
      setNewPointText(prev => ({ ...prev, [taskId]: "" }));
      showToast("Point added");
    } catch (e) { alert("Failed: " + e.message); }
    finally { setPointSaving(false); }
  }, [addPoint, setAllPoints]);

  const handleUpdatePoint = useCallback(async (taskId, pointId, text) => {
    if (!text.trim()) return;
    setPointSaving(true);
    try {
      const data = await updatePoint(taskId, pointId, text.trim());
      setAllPoints(p => ({ ...p, [taskId]: data.points }));
      setEditingPoint(null);
      showToast("Point updated");
    } catch (e) { alert("Failed: " + e.message); }
    finally { setPointSaving(false); }
  }, [updatePoint, setAllPoints]);

  const handleDeletePoint = useCallback(async (taskId, pointId) => {
    setPointSaving(true);
    try {
      const data = await deletePoint(taskId, pointId);
      setAllPoints(p => ({ ...p, [taskId]: data.points }));
      showToast("Point deleted");
    } catch (e) { alert("Failed: " + e.message); }
    finally { setPointSaving(false); }
  }, [deletePoint, setAllPoints]);

  // Normalise a status/priority string: strip emojis & symbols, collapse spaces, lowercase
  const normStr = (s) => s.replace(/[^a-zA-Z0-9\s]/g, "").replace(/\s+/g, " ").toLowerCase().trim();

  const filtered = useMemo(() => {
    if (!records) return [];
    let rows = records;
    if (deferredSearch.trim()) {
      const q = deferredSearch.trim().toLowerCase();
      const blobs = getSearchBlobs();
      rows = rows.filter(r => (blobs.get(r) || "").includes(q));
    }
    if (statusFilter.length > 0) {
      const filterNorms = statusFilter.map(s => normStr(s));
      rows = rows.filter(r => {
        const f = r.fields || {};
        const s = cellText(f["Overall Status"]) || "Not Started";
        const sNorm = normStr(s);
        if (filterNorms.includes(sNorm)) return true;
        // "In Progress" filter also shows overdue tasks that have partial completion
        if (filterNorms.includes("in progress")) {
          if (sNorm.includes("overdue")) {
            const ccc = f["Completed Count Copy"];
            const hasPartial = Array.isArray(ccc) && ccc.length > 0;
            if (hasPartial) return true;
          }
        }
        return false;
      });
    }
    if (priFilter.length > 0) {
      const filterNorms = priFilter.map(p => p.toLowerCase());
      rows = rows.filter(r => filterNorms.includes(cellText((r.fields || {})["Priority"]).toLowerCase()));
    }
    if (employeeFilter.length > 0) {
      const filterLower = employeeFilter.map(e => e.toLowerCase());
      rows = rows.filter(r => {
        const f = r.fields || {};
        // Check Assigned Users
        const assignedRaw = f["Total Assigned Users"] || f["All Assigned Users"] || f["Assigned Users"] || f["User"];
        let assignedNames = [];
        if (Array.isArray(assignedRaw)) {
          assignedNames = assignedRaw.map(v => (v?.en_name || v?.name || "").toLowerCase()).filter(Boolean);
        } else {
          const rawStr = cellText(assignedRaw);
          if (rawStr && rawStr !== "—") assignedNames = rawStr.split(",").map(s => s.trim().toLowerCase()).filter(Boolean);
        }
        return filterLower.every(fe => assignedNames.includes(fe));
      });
    }
    if (creatorFilter.length > 0) {
      const wanted = creatorFilter.map(e => String(e).trim().toLowerCase());
      rows = rows.filter(r => {
        const f = r.fields || {};
        const tid = cellText(f["Task ID"]);
        const raw = (tid && adminCreatorMap[tid]) || f["Task Creator"] || f["Assigned By"];
        let names = [];
        if (Array.isArray(raw)) names = raw.map(v => (v?.en_name || v?.name || "").toLowerCase()).filter(Boolean);
        else {
          const str = cellText(raw);
          if (str && str !== "—") names = str.split(",").map(x => x.trim().toLowerCase()).filter(Boolean);
        }
        // A task has a single creator, so match ANY of the selected people.
        return wanted.some(w => names.includes(w));
      });
    }
    if (companyFilter.length > 0) {
      rows = rows.filter(r => {
        const val = cellText((r.fields || {})["Company Name"]).toLowerCase();
        return companyFilter.some(c => val.includes(c.toLowerCase()));
      });
    }
    if (assigneeDeptFilter.length > 0) {
      rows = rows.filter(r => {
        const val = cellText((r.fields || {})["Assignee Department"]).toLowerCase();
        return assigneeDeptFilter.some(d => val === d.toLowerCase());
      });
    }
    if (creatorDeptFilter.length > 0) {
      rows = rows.filter(r => {
        const val = cellText((r.fields || {})["Creator Department"]).toLowerCase();
        return creatorDeptFilter.some(d => val === d.toLowerCase());
      });
    }
    if (timeFilter !== "all") {
      const now = new Date();
      now.setHours(0,0,0,0);
      const nowMs = now.getTime();
      const tomorrowMs = nowMs + 86400000;
      const next7DaysMs = nowMs + 7 * 86400000;

      rows = rows.filter(r => {
        const end = normalizeToMs((r.fields || {})["End Date"]);
        if (!end) return false;
        if (timeFilter === "today") return end >= nowMs && end < tomorrowMs;
        if (timeFilter === "tomorrow") return end >= tomorrowMs && end < tomorrowMs + 86400000;
        if (timeFilter === "7days") return end >= nowMs && end < next7DaysMs;
        return true;
      });
    }

    if (customStart) {
      const startMs = new Date(customStart + "T00:00:00").getTime();
      rows = rows.filter(r => {
        const s = normalizeToMs((r.fields || {})["Start Date"]) || normalizeToMs((r.fields || {})["End Date"]);
        return s && s >= startMs;
      });
    }

    if (customEnd) {
      const endMs = new Date(customEnd + "T23:59:59").getTime();
      rows = rows.filter(r => {
        const e = normalizeToMs((r.fields || {})["End Date"]);
        return e && e <= endMs;
      });
    }

    return [...rows].sort((a, b) => {
      if (sortCol) {
         let aVal, bVal;
         const headerMap = {
           "Task ID": "Task ID", "Task Name": "Task Name", "Status": "Overall Status",
           "Priority": "Priority", "Assigned To": "Assigned Users", "Supervisor": "Supervisor",
           "Start Date": "Start Date", "Due Date": "End Date", "Company": "Company Name",
           "Department": "Assignee Department", "Recurring": "Recurring Type"
         };
         const fieldKey = headerMap[sortCol] || sortCol;
         
         if (fieldKey === "End Date" || fieldKey === "Start Date") {
             aVal = normalizeToMs((a.fields || {})[fieldKey]) || Infinity;
             bVal = normalizeToMs((b.fields || {})[fieldKey]) || Infinity;
             return sortAsc ? aVal - bVal : bVal - aVal;
         }
         aVal = String(cellText((a.fields || {})[fieldKey]) || "").toLowerCase();
         bVal = String(cellText((b.fields || {})[fieldKey]) || "").toLowerCase();
         return sortAsc ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      
      const isTaskCompleted = (row) => {
        const f = row.fields || {};
        const statusText = (cellText(f["Overall Status"]) || "").toLowerCase();
        
        if (statusText.includes("done") || statusText.includes("✅")) {
          return true;
        }
        
        const assignedRaw = f["Total Assigned Users"] || f["All Assigned Users"] || f["Assigned Users"] || f["User"];
        let assignedNames = [];
        if (Array.isArray(assignedRaw)) {
          assignedNames = assignedRaw.map(v => v.en_name || v.name).filter(Boolean);
        } else {
          const rawStr = cellText(assignedRaw);
          if (rawStr && rawStr !== "—") assignedNames = rawStr.split(",").map(s => s.trim()).filter(Boolean);
        }
        
        const rawCompletedBy = f["Completed Count Copy"] || f["Task Completed By"] || f["Completed By"];
        let completedBy = cellText(rawCompletedBy);
        if (Array.isArray(rawCompletedBy)) {
          const resolvedNames = rawCompletedBy.map(v => {
            if (v?.id) {
              const match = (larkUsers || []).find(u => u.id === v.id);
              if (match) return match.name;
            }
            return null;
          }).filter(Boolean);
          if (resolvedNames.length > 0 && completedBy === "") {
            completedBy = resolvedNames.join(", ");
          }
        }
        
        let completedCount = 0;
        if (completedBy && completedBy !== "—") {
          completedCount = completedBy.split(",").length;
        }
        
        const totalAssignedCount = assignedNames.length || 1;
        const progressPercent = Math.min(100, Math.round((completedCount / totalAssignedCount) * 100));
        
        return progressPercent === 100;
      };

      const compA = isTaskCompleted(a);
      const compB = isTaskCompleted(b);

      if (compA !== compB) {
        return compA ? 1 : -1;
      }

      const dateA = normalizeToMs((a.fields || {})["End Date"]) || 0;
      const dateB = normalizeToMs((b.fields || {})["End Date"]) || 0;

      if (dateA > 0 && dateB > 0) {
        return sortDueAsc ? dateA - dateB : dateB - dateA;
      }
      if (dateA > 0) return -1;
      if (dateB > 0) return 1;
      return 0;
    });
  }, [records, deferredSearch, getSearchBlobs, statusFilter, priFilter, employeeFilter, creatorFilter, adminCreatorMap, companyFilter, assigneeDeptFilter, creatorDeptFilter, timeFilter, customStart, customEnd, sortCol, sortAsc, larkUsers, sortDueAsc]);

  // ── Excel export ──────────────────────────────────────────
  // Exports exactly what the page is showing (i.e. `filtered`, so any active
  // search/filters carry through). Read-only: no records are touched.
  const handleExportExcel = useCallback(() => {
    if (exporting) return;
    setExporting(true);
    // Deferred so the button can paint its "Exporting…" label before the
    // synchronous XLSX write briefly blocks the main thread.
    setTimeout(() => {
      try {
        exportTasksToExcel(filtered, {
          title: title || "Tasks",
          isUserTable: tableId === USER_TABLE,
        });
      } catch (err) {
        console.error("Excel export failed:", err);
        alert("Could not generate the Excel file. Please try again.");
      } finally {
        setExporting(false);
      }
    }, 30);
  }, [exporting, filtered, title, tableId]);

  // ── Gantt date range ──────────────────────────────────────
  const ganttRange = useMemo(() => {
    if (filtered.length === 0) {
      const now = Date.now();
      return { start: now - 7 * 86400000, end: now + 23 * 86400000, totalMs: 30 * 86400000 };
    }
    let minMs = Infinity, maxMs = -Infinity;
    filtered.forEach(r => {
      const s = normalizeToMs((r.fields || {})["Start Date"]);
      const e = normalizeToMs((r.fields || {})["End Date"]);
      if (s > 0 && s < minMs) minMs = s;
      if (e > 0 && e > maxMs) maxMs = e;
    });
    if (minMs === Infinity) minMs = Date.now();
    if (maxMs === -Infinity) maxMs = minMs + 30 * 86400000;
    
    const minDate = new Date(minMs);
    minDate.setHours(0,0,0,0);
    const pad = 7 * 86400000;
    const start = minDate.getTime() - pad;
    const end = maxMs + pad;
    return { start, end, totalMs: end - start };
  }, [filtered]);

  const sc = (status) => {
    const s = (status || "").toLowerCase();
    if (s.includes("overdue")) return { bg: "var(--surface-0)", color: "var(--danger)" };
    if (s.includes("done") || s.includes("complet")) return { bg: "var(--success-muted)", color: "var(--success)" };
    if (s.includes("progress")) return { bg: "var(--warning-muted)", color: "var(--warning)" };
    return { bg: "var(--surface-3)", color: "var(--text-secondary)" };
  };

  // ── Kanban buckets ───────────────────────────────────────
  // 3-col layout (default): Today (today + overdue) / Tomorrow / Upcoming.
  // 5-col layout (All Tasks): Overdue / Today / Tomorrow / Upcoming / Completed.
  const kanbanBuckets = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    const todayStart = d.getTime();
    const tomorrowStart = todayStart + 86400000;
    const dayAfterStart = todayStart + 2 * 86400000;

    const taskIsDone = (r) => {
      const f = r.fields || {};
      const status = (cellText(f["Overall Status"]) || "").toLowerCase();
      if (status.includes("done") || status.includes("complet") || status.includes("✅")) return true;
      // For USER_TABLE: only treat as done when the whole task is fully complete
      // (all users done), not just individual My Completion
      return false;
    };

    // Helper: resolve a task's effective Completed At in ms (with USER_TABLE
    // fallback, same logic as renderKanbanCard).
    const getCompletedMs = (r) => {
      const f = r.fields || {};
      const tid = cellText(f["Task ID"]);
      let raw = f["Completed At"] || f["Completion Time"];
      if (!raw && tid && userCompletionByTaskId[tid]) raw = userCompletionByTaskId[tid];
      return normalizeToMs(raw);
    };
    // Sort an array of tasks by their Completed At, newest first (descending).
    const sortByCompletedDesc = (arr) => arr.sort((a, b) => getCompletedMs(b) - getCompletedMs(a));

    if (kanbanLayout === "5col") {
      const overdue = [], today = [], tomorrow = [], upcoming = [], completed = [];
      for (const r of filtered) {
        if (taskIsDone(r)) { completed.push(r); continue; }
        const end = normalizeToMs((r.fields || {})["End Date"]);
        if (!end || end <= 0) upcoming.push(r);
        else if (end < todayStart) overdue.push(r);
        else if (end < tomorrowStart) today.push(r);
        else if (end < dayAfterStart) tomorrow.push(r);
        else upcoming.push(r);
      }
      // Completed column: sort by completion time, newest → oldest.
      sortByCompletedDesc(completed);
      return { overdue, today, tomorrow, upcoming, completed };
    }

    if (kanbanLayout === "completedDate") {
      // Buckets by COMPLETION DATE: Today / Yesterday / Previous.
      const yesterdayStart = todayStart - 86400000;
      const today = [], yesterday = [], previous = [];
      for (const r of filtered) {
        const ms = getCompletedMs(r);
        if (ms >= todayStart && ms < tomorrowStart) today.push(r);
        else if (ms >= yesterdayStart && ms < todayStart) yesterday.push(r);
        else previous.push(r);
      }
      // All three buckets contain completed tasks → sort descending by completion time.
      sortByCompletedDesc(today);
      sortByCompletedDesc(yesterday);
      sortByCompletedDesc(previous);
      return { today, yesterday, previous };
    }

    if (kanbanLayout === "overdueDate") {
      // Buckets by END DATE: Today / Yesterday / Previous (for overdue lists).
      const yesterdayStart = todayStart - 86400000;
      const today = [], yesterday = [], previous = [];
      for (const r of filtered) {
        const end = normalizeToMs((r.fields || {})["End Date"]);
        if (end >= todayStart && end < tomorrowStart) today.push(r);
        else if (end >= yesterdayStart && end < todayStart) yesterday.push(r);
        else previous.push(r);
      }
      return { today, yesterday, previous };
    }

    if (kanbanLayout === "createdTask9col") {
      // 9 buckets for the Created Tasks page:
      //   Today / Yesterday / Previous   → NORMAL tasks (no recurring type),
      //                                     grouped by creation date (Start Date).
      //   Daily / Weekly / Monthly /
      //   Quarterly / Half Yearly / Yearly → RECURRING source tasks, grouped by type.
      const yesterdayStart = todayStart - 86400000;
      const today = [], yesterday = [], previous = [];
      const daily = [], weekly = [], monthly = [], quarterly = [], halfYearly = [], yearly = [];

      const norm = (t) => String(t || "").toLowerCase().trim();

      for (const r of filtered) {
        const f = r.fields || {};
        const recurType = norm(cellText(f["Recurring Type"]));

        if (recurType === "daily")                                          daily.push(r);
        else if (recurType === "weekly")                                    weekly.push(r);
        else if (recurType === "monthly")                                   monthly.push(r);
        else if (recurType === "quarterly")                                 quarterly.push(r);
        else if (recurType === "half yearly" || recurType === "half-yearly" || recurType === "halfyearly") halfYearly.push(r);
        else if (recurType === "yearly")                                    yearly.push(r);
        else {
          // Normal task → bucket by creation/start date.
          const created = normalizeToMs(f["Start Date"]);
          if (created >= todayStart && created < tomorrowStart)            today.push(r);
          else if (created >= yesterdayStart && created < todayStart)      yesterday.push(r);
          else                                                              previous.push(r);
        }
      }
      return { today, yesterday, previous, daily, weekly, monthly, quarterly, halfYearly, yearly };
    }

    if (kanbanLayout === "recurringType") {
      // Columns ARE the recurring types — no date/status bucketing at all.
      // Used by the Recurring Tasks page, which lists schedules rather than
      // their generated copies.
      const daily = [], weekly = [], monthly = [], quarterly = [], halfYearly = [], yearly = [], other = [];
      const norm = (t) => String(t || "").toLowerCase().trim();
      for (const r of filtered) {
        const recurType = norm(cellText((r.fields || {})["Recurring Type"]));
        if (recurType === "daily")                                          daily.push(r);
        else if (recurType === "weekly")                                    weekly.push(r);
        else if (recurType === "monthly")                                   monthly.push(r);
        else if (recurType === "quarterly")                                 quarterly.push(r);
        else if (recurType === "half yearly" || recurType === "half-yearly" || recurType === "halfyearly") halfYearly.push(r);
        else if (recurType === "yearly")                                    yearly.push(r);
        // Anything unrecognised still gets a column instead of disappearing.
        else                                                                other.push(r);
      }
      return { daily, weekly, monthly, quarterly, halfYearly, yearly, other };
    }

    // 3-col fallback
    const today = [], tomorrow = [], upcoming = [];
    for (const r of filtered) {
      const end = normalizeToMs((r.fields || {})["End Date"]);
      if (!end || end <= 0) upcoming.push(r);
      else if (end < tomorrowStart) today.push(r);
      else if (end < dayAfterStart) tomorrow.push(r);
      else upcoming.push(r);
    }
    return { today, tomorrow, upcoming };
  }, [filtered, kanbanLayout, tableId, userCompletionByTaskId]);

  // Keep the ref in sync so the notification handler can access buckets
  kanbanBucketsRef.current = kanbanBuckets;

  // Accordion state: which columns are expanded.
  // Extra keys for 5col mode are harmless when in 3col mode (just unused).
  const [colExpanded, setColExpanded] = useState({
    overdue: false, today: false, tomorrow: false, upcoming: false, completed: false,
    yesterday: false, previous: false,
    daily: false, weekly: false, monthly: false, quarterly: false, halfYearly: false, yearly: false,
  });
  const toggleCol = (key) => setColExpanded(p => ({ ...p, [key]: !p[key] }));

  // Helper that renders a single Kanban card. Identical visuals to before —
  // just extracted from the inline .map() so all three columns can share it.
  const renderKanbanCard = (row, i) => {
    const f = row.fields || {};
    const rid = row.record_id || i;
    let statusText = cellText(f["Overall Status"]) || "Not Started";
    let isDone = statusText.toLowerCase().includes("done") || statusText.includes("✅");
    const isUserDone = !!f["My Completion"];
    const priority = cellText(f["Priority"]) || "Medium";
    let isOverdue = statusText.toLowerCase().includes("overdue");
    const priL = priority.toLowerCase();
    const isHigh = priL === "high" || priL === "important";
    const isMed = priL === "medium" || priL === "normal";
    let s = sc(statusText);
    const taskId = cellText(f["Task ID"]);
    const taskName = cellText(f["Task Name"]);
    const cardAttachments = taskId ? (taskAttachments[taskId] || []) : [];
    const cardPoints = taskId ? (allPoints[taskId] || []) : [];
    const isPanelOpen = pointsPanel === taskId;
    const desc = cellText(f["Task Description"]);
    // Assignees who no longer exist in Lark. Recurring tasks keep generating
    // work for them, so the card is flagged red for whoever created it.
    const deletedAssignees = getDeletedAssignees(f);
    const hasDeletedAssignee = deletedAssignees.length > 0;
    const cardRecurType = cellText(f["Recurring Type"]);
    const isRecurringCard = !!cardRecurType && cardRecurType !== "—" && cardRecurType !== "Not Applicable";
    const assignedRaw = f["Total Assigned Users"] || f["All Assigned Users"] || f["Assigned Users"] || f["User"];
    let assignedNames = [];
    if (Array.isArray(assignedRaw)) {
      assignedNames = assignedRaw.map(v => {
        if (v.en_name || v.name) return v.en_name || v.name;
        if (v.id) {
          const match = (larkUsers || []).find(u => u.id === v.id);
          if (match) return match.name;
        }
        return null;
      }).filter(Boolean);
    }
    if (assignedNames.length === 0) {
      const rawStr = cellText(assignedRaw);
      if (rawStr && rawStr !== "—") assignedNames = rawStr.split(",").map(x => x.trim()).filter(Boolean);
    }
    const assignedUsersArr = assignedNames;

    const supervisor = cellText(f["Supervisor"]);
    const createdBy = cellText(f["Task Creator"]) !== "—" ? cellText(f["Task Creator"]) : "";
    const startDate = formatDateTime(f["Start Date"]);
    const endDate = formatDateTime(f["End Date"]);
    const company = cellText(f["Company Name"]);
    const dept = cellText(f["Assignee Department"]);
    const creatorDept = cellText(f["Creator Department"]);
    let completedAtRaw = f["Completed At"] || f["Completion Time"];
    if (!completedAtRaw && taskId && userCompletionByTaskId[taskId]) {
      completedAtRaw = userCompletionByTaskId[taskId];
    }
    const completedAt = completedAtRaw ? formatDateTime(completedAtRaw) : "";

    const rawCompletedBy = f["Completed Count Copy"] || f["Task Completed By"] || f["Completed By"];
    let completedBy = cellText(rawCompletedBy);
    if (Array.isArray(rawCompletedBy)) {
      const resolvedNames = rawCompletedBy.map(v => {
        if (v?.id) {
          const match = (larkUsers || []).find(u => u.id === v.id);
          if (match) return match.name;
        }
        return null;
      }).filter(Boolean);
      if (resolvedNames.length > 0 && completedBy === "") {
        completedBy = resolvedNames.join(", ");
      }
    }

    // Build detailed per-user list with IDs (for supervisor reopen)
    const assignedUsersDetailed = [];
    if (Array.isArray(assignedRaw)) {
      assignedRaw.forEach(v => {
        const uName = v.en_name || v.name || "";
        const uId = v.id || v.open_id || "";
        let resolved = uName;
        if (!resolved && uId) {
          const match = (larkUsers || []).find(u => u.id === uId);
          if (match) resolved = match.name;
        }
        if (resolved || uId) assignedUsersDetailed.push({ name: resolved || "Unknown", id: uId });
      });
    }
    // Build set of completed user IDs
    const completedUserIds = new Set();
    if (Array.isArray(rawCompletedBy)) {
      rawCompletedBy.forEach(v => { if (v?.id) completedUserIds.add(v.id); });
    }
    // Check if current user is supervisor or admin for this task
    const isSupervisorForTask = isAdmin || userMatchesField(f["Supervisor"], user);
    // Check if current user created this task (creator gets edit + delete)
    // Use admin record's creator field (reliable) with card's own field as fallback
    const creatorField = (taskId && adminCreatorMap[taskId]) || f["Task Creator"] || f["Assigned By"];
    const isCreator = userMatchesField(creatorField, user);

    let completedCount = 0;
    if (completedBy && completedBy !== "—") completedCount = completedBy.split(",").length;
    const totalAssignedCount = assignedUsersArr.length || 1;
    const progressPercent = Math.min(100, Math.round((completedCount / totalAssignedCount) * 100));

    if (progressPercent === 100) {
      statusText = "✅ Done";
      isDone = true;
      isOverdue = false;
      s = sc(statusText);
    }

    const normalizeToMsLocal = (val) => {
      if (!val) return 0;
      let ms = 0;
      if (typeof val === "number") ms = val;
      else if (typeof val === "object" && val?.value) ms = val.value;
      else if (Array.isArray(val) && val[0]?.value) ms = val[0].value;
      else {
        const dd = new Date(val);
        ms = isNaN(dd.getTime()) ? 0 : dd.getTime();
      }
      if (ms <= 0) return 0;
      if (ms < 1000000) ms = (ms - 25569) * 86400 * 1000;
      return ms;
    };

    const completedTime = normalizeToMsLocal(completedAtRaw);
    const dueTime = normalizeToMsLocal(f["End Date"]);
    const effectivelyDone = tableId === USER_TABLE ? isUserDone : (statusText.toLowerCase().includes("done") || statusText.includes("✅"));
    const isLate = effectivelyDone && completedTime && dueTime && (completedTime > (dueTime + 60000));
    const recurring = cellText(f["Recurring Type"]);

    const isFlashing = flashTaskId && taskId && String(flashTaskId) === String(taskId);

    return (
      <div key={rid} id={taskId ? `kanban-card-${taskId}` : undefined}
        className={`kanban-card${isFlashing ? " card-glow-flash" : ""}`} style={{
        background: (hasDeletedAssignee || isLate) ? "var(--danger-muted)" : "var(--surface-0)",
        borderRadius: "var(--radius-lg)",
        border: hasDeletedAssignee
          ? "2px solid var(--danger)"
          : `1px solid ${isOverdue || isLate ? "var(--danger-border)" : isDone ? "var(--success-border)" : "var(--border-default)"}`,
        boxShadow: "var(--shadow-card)",
        overflow: "hidden",
        transition: "transform var(--duration-spring) var(--ease-spring), box-shadow var(--duration-normal)",
        opacity: (tableId === USER_TABLE ? isUserDone : isDone) ? 0.7 : 1,
        display: "flex", flexDirection: "column",
      }}
        onMouseEnter={e => {
          e.currentTarget.style.transform = "translateY(-2px) perspective(800px) rotateX(1deg)";
          e.currentTarget.style.boxShadow = "var(--shadow-lg)";
          if (taskId) loadAttachments(taskId);
        }}
        onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "var(--shadow-card)"; }}
      >
        <div style={{ height: 3, background: (hasDeletedAssignee || isOverdue || isLate) ? "var(--danger)" : isDone ? "var(--success)" : isHigh ? "var(--warning)" : isMed ? "var(--primary)" : "var(--border-strong)", opacity: 0.8 }} />

        {/* Deleted-assignee banner — names who is gone and says what to do. */}
        {hasDeletedAssignee && (
          <div style={{
            display: "flex", alignItems: "flex-start", gap: 7,
            padding: "8px 12px",
            background: "var(--danger)", color: "#fff",
            fontSize: 11.5, fontWeight: 700, lineHeight: 1.4,
          }}>
            <span style={{ fontSize: 13, lineHeight: 1.1 }}>⚠</span>
            <span>
              {deletedAssignees.map(u => u.name).join(", ")} — deleted from Lark
              <span style={{ display: "block", fontWeight: 600, opacity: 0.92, marginTop: 2 }}>
                {isRecurringCard
                  ? (deletedAssignees.length >= assignedNames.length && assignedNames.length > 0
                      ? "This recurring task keeps creating work for nobody. Delete it, or assign someone else."
                      : "Remove them from this recurring task — the other assignees keep it running.")
                  : "Reassign this task to a current employee."}
              </span>
            </span>
          </div>
        )}
        <div style={{ padding: "var(--space-4) var(--space-4) var(--space-3)", display: "flex", flexDirection: "column", flex: 1 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "var(--space-2)", marginBottom: "var(--space-2-5)" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              {taskId && (
                <div style={{ fontSize: "var(--text-2xs)", fontWeight: 700, color: "var(--primary)", marginBottom: "var(--space-1)", letterSpacing: "var(--tracking-wide)", fontFeatureSettings: "var(--tabular-nums)" }}>
                  {taskId}
                </div>
              )}
              <div style={{
                fontSize: "15px", fontWeight: 800, color: (tableId === USER_TABLE ? isUserDone : isDone) ? "var(--text-disabled)" : "var(--text-primary)",
                lineHeight: "var(--leading-tight)", textDecoration: (tableId === USER_TABLE ? isUserDone : isDone) ? "line-through" : "none",
                letterSpacing: "var(--tracking-tight)",
              }}>
                {taskName || "Untitled Task"}
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-1)", flexShrink: 0 }}>
              <span style={{ padding: "var(--space-0-5) var(--space-2)", borderRadius: "var(--radius-pill)", fontSize: "var(--text-2xs)", fontWeight: 700, background: s.bg, color: s.color, border: `1px solid ${isOverdue ? "var(--danger-border)" : isDone ? "var(--success-border)" : "var(--border-subtle)"}`, whiteSpace: "nowrap" }}>
                {statusText}
              </span>
              {(() => {
                const pb = getPriorityBadge(priority);
                return (
                  <span style={{ padding: "var(--space-0-5) var(--space-2)", borderRadius: "var(--radius-pill)", fontSize: "var(--text-2xs)", fontWeight: 600, background: pb.bg, color: pb.color, whiteSpace: "nowrap", textAlign: "center", border: "1px solid var(--border-subtle)" }}>
                    {pb.label}
                  </span>
                );
              })()}
            </div>
          </div>

          {desc && (
            <div style={{ fontSize: "var(--text-sm)", color: "var(--text-tertiary)", marginBottom: "var(--space-3)", lineHeight: "var(--leading-relaxed)", padding: "var(--space-2) var(--space-2-5)", background: "var(--surface-1)", borderRadius: "var(--radius)", borderLeft: "2px solid var(--border-strong)" }}>
              {desc}
            </div>
          )}

          <div style={{ display: "flex", gap: "var(--space-2)", marginBottom: "var(--space-2-5)", flexWrap: "wrap" }}>
            {startDate && startDate !== "—" && (
              <span style={{ fontSize: "var(--text-xs)", color: "var(--text-tertiary)", background: "var(--surface-1)", padding: "var(--space-1) var(--space-2)", borderRadius: "var(--radius-sm)", display: "flex", alignItems: "center", gap: "var(--space-1)", border: "1px solid var(--border-subtle)" }}>
                📅 {startDate}
              </span>
            )}
            {endDate && endDate !== "—" && (
              <span style={{ fontSize: "var(--text-xs)", fontWeight: isOverdue ? 700 : 400, color: isOverdue ? "var(--danger)" : "var(--text-tertiary)", background: isOverdue ? "var(--danger-muted)" : "var(--surface-1)", padding: "var(--space-1) var(--space-2)", borderRadius: "var(--radius-sm)", display: "flex", alignItems: "center", gap: "var(--space-1)", border: `1px solid ${isOverdue ? "var(--danger-border)" : "var(--border-subtle)"}` }}>
                {isOverdue ? "🔴" : "⏰"} Due {endDate}
              </span>
            )}
            {recurring && recurring !== "—" && recurring !== "Not Applicable" && (
              <span style={{ fontSize: "var(--text-xs)", color: "var(--purple)", background: "var(--purple-muted)", padding: "var(--space-1) var(--space-2)", borderRadius: "var(--radius-sm)", display: "flex", alignItems: "center", gap: "var(--space-1)", border: "1px solid var(--purple-border)" }}>
                🔄 {recurring}
              </span>
            )}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-2)", marginBottom: "var(--space-2-5)" }}>
            {assignedUsersArr.length > 0 && (
              <div style={{ background: "var(--primary-light)", borderRadius: "var(--radius)", padding: "var(--space-2) var(--space-2-5)", gridColumn: "1/-1", border: "1px solid var(--primary-border)" }}>
                <div style={{ fontSize: "var(--text-2xs)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "var(--tracking-wide)", color: "var(--primary)", marginBottom: "var(--space-1)" }}>Assigned To</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-1)" }}>
                  {assignedUsersArr.map((u, idx) => (
                    <span key={idx} style={{ fontSize: "var(--text-sm)", fontWeight: 600, color: "var(--text-primary)", background: "var(--surface-2)", padding: "var(--space-0-5) var(--space-2)", borderRadius: "var(--radius-md)", border: "1px solid var(--border-default)", whiteSpace: "normal", wordBreak: "break-word" }}>{u}</span>
                  ))}
                </div>
              </div>
            )}
            {supervisor && supervisor !== "—" && (
              <div style={{ background: "var(--success-muted)", borderRadius: "var(--radius)", padding: "var(--space-2) var(--space-2-5)", border: "1px solid var(--success-border)" }}>
                <div style={{ fontSize: "var(--text-2xs)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "var(--tracking-wide)", color: "var(--success)", marginBottom: "var(--space-0-5)" }}>Supervisor</div>
                <div style={{ fontSize: "var(--text-sm)", fontWeight: 600, color: "var(--text-primary)", wordBreak: "break-word" }}>{supervisor}</div>
              </div>
            )}
            {createdBy && createdBy !== "—" && (
              <div style={{ background: "var(--surface-1)", borderRadius: "var(--radius)", padding: "var(--space-2) var(--space-2-5)", border: "1px solid var(--border-subtle)" }}>
                <div style={{ fontSize: "var(--text-2xs)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "var(--tracking-wide)", color: "var(--text-tertiary)", marginBottom: "var(--space-0-5)" }}>Task Creator</div>
                <div style={{ fontSize: "var(--text-sm)", fontWeight: 600, color: "var(--text-secondary)", wordBreak: "break-word" }}>{createdBy}</div>
              </div>
            )}
            {company && company !== "—" && (
              <div style={{ background: "var(--surface-1)", borderRadius: "var(--radius)", padding: "var(--space-2) var(--space-2-5)", border: "1px solid var(--border-subtle)" }}>
                <div style={{ fontSize: "var(--text-2xs)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "var(--tracking-wide)", color: "var(--text-tertiary)", marginBottom: "var(--space-0-5)" }}>Company</div>
                <div style={{ fontSize: "var(--text-sm)", fontWeight: 600, color: "var(--text-secondary)", wordBreak: "break-word" }}>{company}</div>
              </div>
            )}
            {dept && dept !== "—" && (
              <div style={{ background: "var(--surface-1)", borderRadius: "var(--radius)", padding: "var(--space-2) var(--space-2-5)", border: "1px solid var(--border-subtle)" }}>
                <div style={{ fontSize: "var(--text-2xs)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "var(--tracking-wide)", color: "var(--text-tertiary)", marginBottom: "var(--space-0-5)" }}>Assignee Dept</div>
                <div style={{ fontSize: "var(--text-sm)", fontWeight: 600, color: "var(--text-secondary)", wordBreak: "break-word" }}>{dept}</div>
              </div>
            )}
            {creatorDept && creatorDept !== "—" && (
              <div style={{ background: "var(--surface-1)", borderRadius: "var(--radius)", padding: "var(--space-2) var(--space-2-5)", border: "1px solid var(--border-subtle)" }}>
                <div style={{ fontSize: "var(--text-2xs)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "var(--tracking-wide)", color: "var(--text-tertiary)", marginBottom: "var(--space-0-5)" }}>Creator Dept</div>
                <div style={{ fontSize: "var(--text-sm)", fontWeight: 600, color: "var(--text-secondary)", wordBreak: "break-word" }}>{creatorDept}</div>
              </div>
            )}
            {completedAt && completedAt !== "—" && (isDone || isUserDone) && (
              <div style={{ background: "var(--success-muted)", borderRadius: "var(--radius)", padding: "var(--space-2) var(--space-2-5)", border: "1px solid var(--success-border)" }}>
                <div style={{ fontSize: "var(--text-2xs)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "var(--tracking-wide)", color: "var(--success)", marginBottom: "var(--space-0-5)" }}>Completed At</div>
                <div style={{ fontSize: "var(--text-sm)", fontWeight: 600, color: "var(--text-primary)" }}>{completedAt}</div>
              </div>
            )}
            {assignedUsersDetailed.length > 0 && completedCount > 0 ? (
              <div style={{ background: isDone ? "var(--success-muted)" : "var(--warning-muted)", borderRadius: "var(--radius)", padding: "var(--space-2) var(--space-2-5)", gridColumn: "1/-1", border: `1px solid ${isDone ? "var(--success-border)" : "var(--warning-border)"}` }}>
                <div style={{ fontSize: "var(--text-2xs)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "var(--tracking-wide)", color: isDone ? "var(--success)" : "var(--warning)", marginBottom: "var(--space-1)" }}>
                  {isDone ? "✅ Completion Status" : "⏳ Completion Status"}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {assignedUsersDetailed.map((u, idx) => {
                    const userDone = completedUserIds.has(u.id);
                    const rKey = `${taskId}-${u.id}`;
                    return (
                      <div key={idx} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6, padding: "3px 0", borderBottom: idx < assignedUsersDetailed.length - 1 ? "1px solid rgba(0,0,0,0.06)" : "none" }}>
                        <span style={{ fontSize: "var(--text-xs)", fontWeight: 600, color: userDone ? "var(--success)" : "var(--text-secondary)", display: "flex", alignItems: "center", gap: 4 }}>
                          {userDone ? "✅" : "⏳"} {u.name}
                        </span>
                        {userDone && isSupervisorForTask && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleReopenUser(taskId, u.id, u.name); }}
                            disabled={reopening[rKey]}
                            style={{
                              padding: "2px 8px", borderRadius: 6, border: "1px solid var(--warning-border)",
                              fontSize: 10, fontWeight: 700, cursor: reopening[rKey] ? "wait" : "pointer",
                              background: "var(--warning-muted)", color: "var(--warning)",
                              fontFamily: "inherit", transition: "var(--transition-fast)",
                              opacity: reopening[rKey] ? 0.6 : 1,
                            }}
                          >
                            {reopening[rKey] ? "..." : "↺ Reopen"}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : completedBy && completedBy !== "—" && (
              <div style={{ background: isDone ? "var(--success-muted)" : "var(--warning-muted)", borderRadius: "var(--radius)", padding: "var(--space-2) var(--space-2-5)", gridColumn: "1/-1", border: `1px solid ${isDone ? "var(--success-border)" : "var(--warning-border)"}` }}>
                <div style={{ fontSize: "var(--text-2xs)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "var(--tracking-wide)", color: isDone ? "var(--success)" : "var(--warning)", marginBottom: "var(--space-1)" }}>
                  {isDone ? "✅ Completed By" : "⏳ Completed By (Partial)"}
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-1)" }}>
                  {completedBy.split(",").map((name, idx) => (
                    <span key={idx} style={{ fontSize: "var(--text-xs)", fontWeight: 600, color: isDone ? "var(--success)" : "var(--warning)", background: "var(--surface-2)", padding: "var(--space-0-5) var(--space-2)", borderRadius: "var(--radius-pill)", border: `1px solid ${isDone ? "var(--success-border)" : "var(--warning-border)"}`, whiteSpace: "normal", wordBreak: "break-word" }}>
                      {name.trim()}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* ── Points Section ─────────────────────────────── */}
          {(() => {
            const canEditAnyPoint = isAdmin || cardPoints.some(pt => pt.authorId === user?.userId);
            return (
            <div style={{ marginTop: "var(--space-2)", padding: "0 var(--space-1)" }}>
              {cardPoints.length > 0 && (
                <div style={{ marginBottom: 8, padding: "var(--space-2) var(--space-2-5)", background: "var(--surface-1)", borderRadius: "var(--radius)", border: "1px solid var(--border-subtle)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: "var(--purple)", textTransform: "uppercase", letterSpacing: "0.06em" }}>📌 Points ({cardPoints.length})</span>
                    {canEditAnyPoint && (
                      <button type="button" onClick={(e) => { e.stopPropagation(); setPointsPanel(isPanelOpen ? null : taskId); }} style={{
                        fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: 5,
                        border: "1px solid var(--border-default)", background: "var(--surface-2)",
                        color: "var(--primary)", cursor: "pointer", fontFamily: "inherit",
                      }}>{isPanelOpen ? "Close" : "Manage"}</button>
                    )}
                  </div>
                  {!isPanelOpen ? (
                    <ul style={{ margin: 0, paddingLeft: 16, display: "flex", flexDirection: "column", gap: 3 }}>
                      {cardPoints.map(pt => (
                        <li key={pt.id} style={{ fontSize: "var(--text-xs)", color: "var(--text-secondary)", lineHeight: 1.4, wordBreak: "break-word" }}>
                          {pt.text}
                          <span style={{ fontSize: 9, color: "var(--gray-400)", marginLeft: 6 }}>— {pt.authorName}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {cardPoints.map(pt => {
                        const canManageThis = isAdmin || pt.authorId === user?.userId;
                        return (
                        <div key={pt.id} style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 0", borderBottom: "1px solid var(--border-subtle)" }}>
                          {editingPoint?.pointId === pt.id && editingPoint?.taskId === taskId ? (
                            <form onSubmit={(e) => { e.preventDefault(); handleUpdatePoint(taskId, pt.id, editingPoint.text); }} style={{ display: "flex", flex: 1, gap: 4 }}>
                              <input type="text" value={editingPoint.text} onChange={e => setEditingPoint(p => ({ ...p, text: e.target.value }))}
                                autoFocus style={{ flex: 1, fontSize: 12, padding: "4px 8px", borderRadius: 6, border: "1px solid var(--primary-border)", outline: "none", fontFamily: "inherit", background: "var(--surface-0)" }} />
                              <button type="submit" disabled={pointSaving} style={{ fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 5, border: "none", background: "var(--primary)", color: "white", cursor: "pointer", fontFamily: "inherit" }}>Save</button>
                              <button type="button" onClick={() => setEditingPoint(null)} style={{ fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 5, border: "1px solid var(--border-default)", background: "var(--surface-2)", color: "var(--text-secondary)", cursor: "pointer", fontFamily: "inherit" }}>✕</button>
                            </form>
                          ) : (
                            <>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <span style={{ fontSize: "var(--text-xs)", color: "var(--text-secondary)", lineHeight: 1.4, wordBreak: "break-word" }}>• {pt.text}</span>
                                <div style={{ fontSize: 9, color: "var(--gray-400)", marginTop: 1 }}>By {pt.authorName}</div>
                              </div>
                              {canManageThis && (
                                <>
                                  <button type="button" onClick={(e) => { e.stopPropagation(); setEditingPoint({ taskId, pointId: pt.id, text: pt.text }); }}
                                    style={{ fontSize: 10, padding: "2px 6px", borderRadius: 4, border: "1px solid var(--border-default)", background: "var(--surface-2)", color: "var(--primary)", cursor: "pointer", fontFamily: "inherit", flexShrink: 0 }}>✏️</button>
                                  <button type="button" onClick={(e) => { e.stopPropagation(); handleDeletePoint(taskId, pt.id); }}
                                    style={{ fontSize: 10, padding: "2px 6px", borderRadius: 4, border: "1px solid var(--danger-border)", background: "var(--danger-muted)", color: "var(--danger)", cursor: "pointer", fontFamily: "inherit", flexShrink: 0 }}>🗑️</button>
                                </>
                              )}
                            </>
                          )}
                        </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              {/* Add Point inline */}
              <form onSubmit={(e) => { e.preventDefault(); if (taskId) handleAddPoint(taskId, newPointText[taskId] || ""); }}
                style={{ display: "flex", gap: 4, marginBottom: 4 }}>
                <input type="text" placeholder="Add a point..."
                  value={newPointText[taskId] || ""}
                  onChange={e => setNewPointText(prev => ({ ...prev, [taskId]: e.target.value }))}
                  style={{ flex: 1, fontSize: 12, padding: "6px 10px", borderRadius: 8, border: "1px solid var(--border-default)", outline: "none", fontFamily: "inherit", background: "var(--surface-1)", transition: "border 0.15s" }} />
                <button type="submit" disabled={pointSaving || !(newPointText[taskId] || "").trim()}
                  style={{ fontSize: 11, fontWeight: 700, padding: "6px 12px", borderRadius: 8, border: "none",
                    background: (newPointText[taskId] || "").trim() ? "var(--primary)" : "var(--surface-3)",
                    color: (newPointText[taskId] || "").trim() ? "white" : "var(--text-disabled)",
                    cursor: (newPointText[taskId] || "").trim() ? "pointer" : "default", fontFamily: "inherit", transition: "all 0.15s",
                  }}>+ Add</button>
              </form>
            </div>
            );
          })()}

          {cardAttachments.length > 0 && (
            <div style={{ marginTop: "var(--space-3)", padding: "var(--space-2-5) var(--space-3)", background: "var(--surface-1)", borderRadius: "var(--radius)", border: "1px solid var(--border-default)" }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "var(--gray-500)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8, display: "flex", alignItems: "center", gap: 4 }}>
                <span>📎 Attachments</span>
                <span style={{ fontSize: 9, fontWeight: 400, color: "var(--gray-400)" }}>({cardAttachments.length})</span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {cardAttachments.map((a, idx) => (
                  <div key={idx} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "4px 0", borderBottom: idx === cardAttachments.length - 1 ? "none" : "1px solid var(--gray-100)" }}>
                    <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                      <a href={`https://aquint-task-management.aged-wildflower-4467.workers.dev/api/download/${a.fileToken}`}
                        target="_blank" rel="noopener noreferrer"
                        style={{ fontSize: 12, fontWeight: 600, color: "var(--primary)", textDecoration: "none", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>
                        📄 {a.fileName}
                      </a>
                      <div style={{ fontSize: 9, color: "var(--gray-400)", marginTop: 1 }}>By {a.uploaderName || "Unknown"}</div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <a href={`https://aquint-task-management.aged-wildflower-4467.workers.dev/api/download/${a.fileToken}`}
                        target="_blank" rel="noopener noreferrer" className="btn-icon" style={{ padding: 4, fontSize: 12 }} title="Download">📥</a>
                      {(isAdmin || (user?.name && a.uploaderName === user.name)) && (
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (window.confirm(`Delete attachment "${a.fileName}"?`)) {
                              try {
                                const idForDelete = taskId && taskId !== "—" ? taskId : rid;
                                await deleteAttachment(idForDelete, a.fileToken);
                                // Remove from local state instantly (no full reload)
                                setTaskAttachments(prev => ({
                                  ...prev,
                                  [idForDelete]: (prev[idForDelete] || []).filter(att => att.fileToken !== a.fileToken)
                                }));
                                // Show success toast
                                showToast(`"${a.fileName}" deleted successfully`);
                              } catch (err) { alert(err.message); }
                            }
                          }}
                          className="btn-icon" style={{ padding: 4, fontSize: 12, color: "var(--danger)" }} title="Delete Attachment">🗑️</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(showActions || isCreator) && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12, paddingTop: 12, borderTop: "1px solid var(--gray-100)", marginTop: "auto" }}>
              {assignedUsersArr.length > 0 && (
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <span style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--gray-500)" }}>Task Progress</span>
                    <span style={{ fontSize: 11, fontWeight: 800, color: progressPercent === 100 ? "#10B981" : "var(--primary)" }}>
                      {progressPercent}% <span style={{ fontSize: 9, color: "var(--gray-400)" }}>({completedCount}/{totalAssignedCount})</span>
                    </span>
                  </div>
                  <div style={{ width: "100%", height: 8, background: "var(--surface-3)", borderRadius: 10, overflow: "hidden", boxShadow: "inset 0 1px 2px rgba(0,0,0,0.08)", position: "relative" }}>
                    <div style={{
                      width: `${progressPercent}%`, height: "100%",
                      background: progressPercent === 100 ? "linear-gradient(90deg, var(--success), #34D399)" : "linear-gradient(90deg, var(--primary), #818CF8)",
                      borderRadius: 10,
                      transition: "width 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                      boxShadow: progressPercent === 100 ? "var(--shadow-glow-success)" : "var(--shadow-glow-primary)",
                    }} />
                  </div>
                </div>
              )}

              <div className={`card-actions-row${(tableId === USER_TABLE && isUserDone && !isDone) ? " has-completed-badge" : ""}`} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                {canComplete && (() => {
                  const userDoneButTaskPending = tableId === USER_TABLE && isUserDone && !isDone;
                  const showReopen = tableId === USER_TABLE ? (isUserDone && isDone) : isDone;

                  if (userDoneButTaskPending) {
                    return (
                      <>
                        <div className="reopen-completed-wrap" style={{ display: "flex", flex: "1 1 0", minWidth: 0, gap: 6 }}>
                          <div className="card-completed-badge" style={{
                            flex: "1 1 0", minWidth: 0, padding: "10px 14px", borderRadius: 10,
                            background: "linear-gradient(135deg, var(--success), #059669)",
                            color: "white", fontFamily: "inherit",
                            boxShadow: "0 2px 10px rgba(16,185,129,0.2)",
                            display: "flex", alignItems: "center", justifyContent: "center", gap: 7, height: 40, boxSizing: "border-box",
                          }}>
                            <span style={{ fontSize: 13, fontWeight: 700, whiteSpace: "nowrap" }}>✅ You've Completed</span>
                          </div>
                          <button
                            type="button"
                            className="reopen-btn-mobile"
                            onClick={() => handleComplete(row)}
                            disabled={completing[rid]}
                            title="Reopen task"
                            style={{
                              width: "100%", height: 40, borderRadius: 10, border: "1px solid var(--border-default)",
                              fontSize: 13, fontWeight: 700, cursor: completing[rid] ? "wait" : "pointer", fontFamily: "inherit", padding: 0,
                              background: "var(--surface-1)", color: "var(--warning)",
                              boxShadow: "var(--shadow-xs)",
                              display: "none", alignItems: "center", justifyContent: "center", gap: 6,
                            }}
                          >
                            {completing[rid] ? "⏳ Reopening..." : "↺ Reopen"}
                          </button>
                        </div>
                        <button
                          type="button"
                          className="reopen-btn-desktop"
                          onClick={() => handleComplete(row)}
                          disabled={completing[rid]}
                          title="Reopen task"
                          style={{
                            width: 40, height: 40, borderRadius: 10, border: "1px solid var(--border-default)",
                            fontSize: 15, cursor: completing[rid] ? "wait" : "pointer", fontFamily: "inherit", flexShrink: 0, padding: 0,
                            background: "var(--surface-1)", color: "var(--warning)",
                            boxShadow: "var(--shadow-xs)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                          }}
                        >
                          {completing[rid] ? "⏳" : "↺"}
                        </button>
                      </>
                    );
                  }

                  return (
                    <button
                      type="button"
                      onClick={() => handleComplete(row)}
                      disabled={completing[rid]}
                      className="card-action-btn-complete-main"
                      style={{
                        flex: "1 1 0", minWidth: 0, height: 40, padding: "0 16px", borderRadius: 10,
                        border: "none", fontSize: 13, fontWeight: 700,
                        cursor: completing[rid] ? "wait" : "pointer", fontFamily: "inherit",
                        background: completing[rid]
                          ? "var(--surface-3)"
                          : showReopen
                            ? "linear-gradient(135deg, var(--warning), #D97706)"
                            : "linear-gradient(135deg, var(--primary), var(--primary-dark))",
                        color: completing[rid] ? "var(--text-disabled)" : "white",
                        transition: "all 0.2s cubic-bezier(0.4,0,0.2,1)",
                        boxShadow: completing[rid]
                          ? "none"
                          : showReopen
                            ? "0 2px 10px rgba(217,119,6,0.25)"
                            : "0 2px 10px rgba(79,70,229,0.25)",
                        display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                        letterSpacing: "0.02em", boxSizing: "border-box",
                      }}
                    >
                      {completing[rid] ? "⏳ Saving..." : showReopen ? "↺ Reopen" : "✓ Mark as Done"}
                    </button>
                  );
                })()}
                <input type="file" multiple style={{ display: "none" }} id={`attach-${rid}`} onChange={(e) => {
                  if (e.target.files?.length && taskId) {
                    Array.from(e.target.files).forEach(file => handleAttachFile(taskId, file));
                    e.target.value = null;
                  }
                }} />
                <div className="card-btns-group" style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <button type="button" className="card-action-btn" aria-label="Attach file" onClick={(e) => { e.preventDefault(); document.getElementById(`attach-${rid}`).click(); }} style={{
                    width: 40, height: 40, borderRadius: 10, border: "1px solid var(--border-default)",
                    fontSize: 15, cursor: "pointer", fontFamily: "inherit", flexShrink: 0, padding: 0,
                    background: "var(--surface-1)", color: "var(--text-secondary)",
                    boxShadow: "var(--shadow-xs)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>📎</button>
                  <button type="button" className={`card-action-btn${flashTaskId && taskId && String(flashTaskId) === String(taskId) ? " comment-btn-flash" : ""}`} aria-label="Comments" onClick={(e) => { e.preventDefault(); setCommentTask(row); }}
                    style={{
                      width: 40, height: 40, borderRadius: 10, border: "1px solid var(--border-default)",
                      fontSize: 15, cursor: "pointer", fontFamily: "inherit", position: "relative", flexShrink: 0, padding: 0,
                      background: "var(--surface-1)", color: "var(--text-secondary)",
                      boxShadow: "var(--shadow-xs)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                    💬
                    {(() => {
                      const total = commentCounts?.[taskId] || 0;
                      const read = readComments?.[taskId] || 0;
                      const unread = Math.max(0, total - read);
                      return unread > 0 && (
                        <span style={{
                          position: "absolute", top: -5, right: -5,
                          background: "var(--danger)", color: "white",
                          fontSize: 9, fontWeight: 800, padding: "2px 5px",
                          borderRadius: 10, boxShadow: "var(--shadow-glow-danger)"
                        }}>{unread}</span>
                      );
                    })()}
                  </button>
                  {(canEdit || isCreator) && (
                    <button type="button" className="card-action-btn" aria-label="Edit task" onClick={(e) => { e.preventDefault(); const adminRec = taskId && adminRecordByTaskId[taskId]; setEditRecord(adminRec || row); }} style={{
                      height: 40, padding: "0 12px", borderRadius: 10, border: "1px solid var(--border-default)",
                      fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", flexShrink: 0,
                      background: "var(--surface-1)", color: "var(--primary)",
                      boxShadow: "var(--shadow-xs)",
                      display: "flex", alignItems: "center", justifyContent: "center", gap: 4,
                    }}>✏️ Edit</button>
                  )}
                  {(canDelete || isCreator) && (
                    <button type="button" className="card-action-btn card-action-btn-danger" aria-label="Delete task" onClick={(e) => { e.preventDefault(); const adminRec = taskId && adminRecordByTaskId[taskId]; setDeleteRecord(adminRec || row); }} style={{
                      width: 40, height: 40, borderRadius: 10, border: "1px solid var(--danger-border)",
                      fontSize: 15, cursor: "pointer", fontFamily: "inherit", flexShrink: 0, padding: 0,
                      background: "var(--danger-muted)", color: "var(--danger)",
                      boxShadow: "var(--shadow-xs)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>🗑️</button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="task-table-root">
      <style>
        {`
          @keyframes gaming-stripes {
            0% { background-position: 0 0; }
            100% { background-position: 28px 0; }
          }

          /* Scoped Toolbar Styles */
          .task-toolbar {
            display: flex;
            flex-direction: column;
            gap: 12px;
            margin-bottom: 20px;
            width: 100%;
          }

          .toolbar-row {
            display: flex;
            align-items: center;
            gap: 10px;
            width: 100%;
          }

          /* All 6 filters in one row, equal flex */
          .row-1 .filter-dropdown-wrapper {
            flex: 1;
            min-width: 120px;
          }
          .row-1 {
            flex-wrap: wrap;
          }

          /* Row 2: all three dropdowns equal flex */
          .row-2 .filter-dropdown-wrapper {
            flex: 1;
            min-width: 0;
          }

          .date-filter-panel {
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: var(--space-3);
            row-gap: var(--space-2-5);
            flex: 1;
            min-width: 0;
            background: var(--surface-1);
            border: 1px solid var(--border-default);
            border-radius: var(--radius);
            padding: var(--space-2) var(--space-3);
            box-shadow: var(--shadow-xs);
          }
          
          .date-section-title {
            display: flex;
            align-items: center;
            gap: 6px;
            font-size: 11px;
            font-weight: 800;
            color: var(--gray-500);
            text-transform: uppercase;
            letter-spacing: 0.05em;
            white-space: nowrap;
          }
          
          .date-presets-group {
            display: flex;
            border: 1px solid var(--border-default);
            border-radius: var(--radius-sm);
            overflow: hidden;
            background: var(--surface-1);
            box-shadow: var(--shadow-xs);
            flex-shrink: 0;
          }
          .preset-btn {
            padding: var(--space-1-5) var(--space-3);
            border: none;
            background: transparent;
            color: var(--text-tertiary);
            font-size: var(--text-xs);
            font-weight: 700;
            cursor: pointer;
            font-family: inherit;
            transition: var(--transition-fast);
            white-space: nowrap;
          }
          .preset-btn+.preset-btn {
            border-left: 1px solid var(--border-subtle);
          }
          .preset-btn:hover {
            background-color: var(--surface-3);
            color: var(--primary);
          }
          .preset-btn.active {
            background: var(--primary) !important;
            color: var(--surface-0) !important;
          }
          
          .date-divider {
            width: 1.5px;
            height: 20px;
            background-color: var(--border-default);
            flex-shrink: 0;
          }

          .custom-date-picker-group {
            display: flex;
            align-items: center;
            gap: 8px;
            flex-shrink: 0;
          }
          .calendar-input {
            border: 1.5px solid var(--border-default);
            border-radius: 6px;
            padding: 5px 10px;
            font-size: 12px;
            outline: none;
            color: var(--text-secondary);
            font-family: inherit;
            cursor: pointer;
            background-color: var(--surface-1);
            color-scheme: light;
            transition: all 0.2s;
            min-height: 32px;
          }
          .calendar-input:hover, .calendar-input:focus {
            border-color: var(--primary);
            box-shadow: 0 0 0 2px var(--primary-glow);
          }
          .date-to-separator {
            font-size: 11.5px;
            color: var(--text-disabled);
            font-weight: 600;
          }
          .clear-date-btn {
            background: rgba(239, 68, 68, 0.1);
            border: 1px solid var(--danger);
            color: var(--danger);
            border-radius: 6px;
            font-size: 10px;
            font-weight: 800;
            cursor: pointer;
            padding: 5px 10px;
            display: flex;
            align-items: center;
            text-transform: uppercase;
            letter-spacing: 0.04em;
            transition: all 0.15s ease;
          }
          .clear-date-btn:hover {
            background: var(--danger);
            color: white;
          }

          .task-count-badge {
            margin-left: auto;
            min-height: 38px;
            padding: 0 var(--space-3-5);
            background: var(--surface-2);
            border: 1px solid var(--border-default);
            border-radius: var(--radius);
            font-size: var(--text-base);
            color: var(--text-secondary);
            display: flex;
            align-items: center;
            justify-content: center;
            white-space: nowrap;
            font-weight: 700;
            box-shadow: var(--shadow-xs);
            flex-shrink: 0;
            font-feature-settings: var(--tabular-nums);
          }

          .bulk-action-bar {
            display: flex; align-items: center; gap: var(--space-3);
            flex-wrap: wrap; width: 100%;
            padding: 10px 14px;
            border: 1px solid var(--warning);
            background: var(--warning-muted);
            border-radius: var(--radius);
          }
          .bulk-action-text { font-size: 12.5px; color: var(--text-secondary); }
          .bulk-action-btn {
            margin-left: auto;
            padding: var(--space-2) var(--space-3-5);
            min-height: 34px;
            border: 1px solid var(--danger);
            border-radius: var(--radius);
            background: var(--surface-0);
            color: var(--danger);
            font-size: 12.5px; font-weight: 700; font-family: inherit;
            cursor: pointer; white-space: nowrap;
            transition: var(--transition-fast);
          }
          .bulk-action-btn:hover { background: var(--danger); color: #fff; }
          @media (max-width: 768px) {
            .bulk-action-btn { margin-left: 0; width: 100%; }
          }
          /* ── Search box + typeahead ── */
          .row-search {
            width: 100%;
          }
          .row-search .search-box-wrapper {
            flex: 1 1 100%;
            width: 100%;
            min-width: 0;
          }
          .search-box-wrapper {
            position: relative;
            display: flex;
            align-items: center;
            flex: 2 1 240px;
            min-width: 180px;
          }
          .search-icon {
            position: absolute;
            left: 11px;
            font-size: 12px;
            opacity: 0.6;
            pointer-events: none;
          }
          .search-input {
            width: 100%;
            min-height: 38px;
            padding: var(--space-2) 32px var(--space-2) 32px;
            border: 1px solid var(--border-default);
            border-radius: var(--radius);
            background: var(--surface-1);
            color: var(--text-primary);
            font-size: var(--text-sm);
            font-family: inherit;
            font-weight: 600;
            box-shadow: var(--shadow-xs);
            transition: var(--transition-fast);
          }
          .search-input::placeholder { color: var(--text-disabled); font-weight: 500; }
          .search-input:focus {
            outline: none;
            border-color: var(--primary);
            box-shadow: 0 0 0 3px var(--primary-light);
          }
          .search-clear {
            position: absolute;
            right: 8px;
            width: 20px; height: 20px;
            display: flex; align-items: center; justify-content: center;
            border: none; border-radius: 50%;
            background: var(--surface-3);
            color: var(--text-tertiary);
            font-size: 14px; line-height: 1;
            cursor: pointer; font-family: inherit;
            transition: var(--transition-fast);
          }
          .search-clear:hover { background: var(--danger-muted); color: var(--danger); }
          .search-suggestions {
            position: absolute;
            top: calc(100% + 6px);
            left: 0; right: 0;
            z-index: 60;
            max-height: 320px;
            overflow-y: auto;
            background: var(--surface-1);
            border: 1px solid var(--border-default);
            border-radius: var(--radius);
            box-shadow: var(--shadow-xl);
          }
          .search-sug {
            display: flex;
            align-items: center;
            gap: 9px;
            width: 100%;
            padding: 9px 12px;
            border: none;
            border-bottom: 1px solid var(--border-subtle);
            background: transparent;
            color: var(--text-primary);
            font-family: inherit;
            font-size: 12.5px;
            font-weight: 600;
            text-align: left;
            cursor: pointer;
          }
          .search-sug:last-child { border-bottom: none; }
          .search-sug.active { background: var(--primary-light); }
          .sug-icon { font-size: 13px; flex-shrink: 0; }
          .sug-text {
            flex: 1; min-width: 0;
            overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
          }
          .sug-meta {
            flex-shrink: 0;
            font-size: 10px; font-weight: 700;
            color: var(--text-tertiary);
            background: var(--surface-3);
            padding: 2px 7px;
            border-radius: var(--radius-pill);
          }
          .search-empty {
            padding: 14px 12px;
            font-size: 12px;
            color: var(--text-tertiary);
            text-align: center;
          }
          .header-actions-group {
            display: flex;
            align-items: center;
            gap: var(--space-2);
            flex-wrap: wrap;
          }
          .export-excel-btn {
            display: inline-flex;
            align-items: center;
            gap: var(--space-1-5);
            padding: var(--space-2) var(--space-3-5);
            min-height: 38px;
            border: 1px solid var(--border-default);
            border-radius: var(--radius);
            background: var(--surface-1);
            color: var(--success, #1a7f4b);
            font-size: var(--text-sm);
            font-weight: 700;
            font-family: inherit;
            white-space: nowrap;
            cursor: pointer;
            box-shadow: var(--shadow-xs);
            transition: var(--transition-fast);
          }
          .export-excel-btn:hover:not(:disabled) {
            background: var(--success-muted, var(--surface-3));
            border-color: var(--success, var(--border-default));
          }
          .export-excel-btn:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }
          .view-switcher-group {
            display: flex;
            border: 1px solid var(--border-default);
            border-radius: var(--radius);
            overflow: hidden;
            background: var(--surface-1);
            box-shadow: var(--shadow-xs);
            width: max-content;
          }
          .switcher-btn {
            padding: var(--space-2) var(--space-3-5);
            border: none;
            background: transparent;
            color: var(--text-tertiary);
            font-size: var(--text-sm);
            font-weight: 700;
            cursor: pointer;
            font-family: inherit;
            transition: var(--transition-fast);
            display: flex;
            align-items: center;
            gap: var(--space-1-5);
            white-space: nowrap;
            min-height: 38px;
          }
          .switcher-btn+.switcher-btn {
            border-left: 1px solid var(--border-subtle);
          }
          .switcher-btn:hover {
            background-color: var(--surface-3);
            color: var(--primary);
          }
          .switcher-btn.active {
            background: var(--primary) !important;
            color: var(--surface-0) !important;
          }

          /* Grid view: allow td cells to expand to their content
             (overrides global td rule that clips at 200px with ellipsis) */
          .task-grid-table td {
            max-width: none;
            overflow: visible;
            text-overflow: clip;
          }

          /* Comment-icon flash, triggered when a notification is clicked.
             Runs for ~2 s total (0.5 s × 4 iterations). */
          @keyframes commentBtnFlash {
            0%, 100% {
              background: var(--surface-1);
              color: var(--purple);
              box-shadow: var(--shadow-xs);
              transform: scale(1);
            }
            50% {
              background: var(--primary);
              color: var(--surface-0);
              box-shadow: var(--shadow-glow-primary);
              transform: scale(1.15);
            }
          }
          .comment-btn-flash {
            animation: commentBtnFlash 0.5s ease-in-out 4 !important;
          }

          /* Card glow — smooth breathing pulse after chat panel closes */
          @keyframes cardGlowFlash {
            0% {
              box-shadow: var(--shadow-card);
              border-color: var(--border-default);
              transform: scale(1);
            }
            30% {
              box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.5), 0 0 24px rgba(79, 70, 229, 0.3), 0 4px 16px rgba(79, 70, 229, 0.15);
              border-color: var(--primary);
              transform: scale(1.008);
            }
            70% {
              box-shadow: 0 0 0 2px rgba(79, 70, 229, 0.3), 0 0 16px rgba(79, 70, 229, 0.2);
              border-color: var(--primary);
              transform: scale(1.004);
            }
            100% {
              box-shadow: var(--shadow-card);
              border-color: var(--border-default);
              transform: scale(1);
            }
          }
          .card-glow-flash {
            animation: cardGlowFlash 1s cubic-bezier(0.4, 0, 0.2, 1) 3 !important;
            z-index: 2;
            position: relative;
          }

          /* ── Card Action Button Hover Effects ── */
          .card-action-btn {
            transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
            position: relative;
            overflow: hidden;
          }
          .card-action-btn::after {
            content: '';
            position: absolute; inset: 0;
            background: currentColor;
            opacity: 0;
            transition: opacity 0.2s ease;
            border-radius: inherit;
            pointer-events: none;
          }
          .card-action-btn:hover {
            transform: translateY(-2px) !important;
            box-shadow: 0 4px 12px rgba(0,0,0,0.12) !important;
          }
          .card-action-btn:hover::after { opacity: 0.08; }
          .card-action-btn:active {
            transform: translateY(0) scale(0.97) !important;
            box-shadow: 0 1px 4px rgba(0,0,0,0.1) !important;
          }
          .card-action-btn:active::after { opacity: 0.12; }

          .card-action-btn-danger:hover {
            border-color: var(--danger) !important;
            box-shadow: 0 4px 12px rgba(239,68,68,0.2) !important;
          }

          .card-action-btn-complete-main:hover {
            transform: translateY(-2px) !important;
            box-shadow: 0 6px 20px rgba(79,70,229,0.35) !important;
            filter: brightness(1.08);
          }
          .card-action-btn-complete-main:active {
            transform: translateY(0) scale(0.98) !important;
            filter: brightness(0.95);
          }

          /* Mobile: main action on row 1, icon buttons on row 2 */
          @media (max-width: 600px) {
            .card-actions-row {
              flex-wrap: wrap !important;
            }
            .card-actions-row .card-completed-badge,
            .card-actions-row .card-action-btn-complete-main {
              flex: 1 1 100% !important;
              width: 100% !important;
            }
            .card-actions-row .card-btns-group {
              flex: 1 1 100% !important;
            }
            .card-actions-row .card-btns-group button {
              flex: 1 !important;
              min-width: 0 !important;
              width: auto !important;
              height: 42px !important;
            }
          }

          @keyframes toastSlideUp {
            from { opacity: 0; transform: translateX(-50%) translateY(20px); }
            to   { opacity: 1; transform: translateX(-50%) translateY(0); }
          }

          /* ── Kanban Accordion Layout ── */
          .task-table-root {
            display: flex;
            flex-direction: column;
            min-height: 0;
          }
          /* ── Accordion Kanban (desktop + mobile) ── */
          .kanban-three-col-wrap,
          .kanban-three-col-wrap.layout-5col,
          .kanban-three-col-wrap.layout-createdTask9col {
            display: flex;
            flex-direction: column;
            gap: 10px;
            width: 100%;
            flex: 0 0 auto;
            align-items: stretch;
            min-height: 0;
          }
          .kanban-col {
            flex: 0 0 auto;
            width: 100%;
            max-width: 100%;
            min-width: 0;
            display: flex;
            flex-direction: column;
            background: var(--surface-1);
            border: 1px solid var(--border-default);
            border-radius: var(--radius-lg);
            overflow: hidden;
            min-height: auto;
            backdrop-filter: blur(var(--blur-sm));
            transition: box-shadow 0.2s;
          }
          .kanban-col.expanded {
            box-shadow: var(--shadow-sm);
          }
          .kanban-col-header {
            display: flex;
            align-items: center;
            gap: var(--space-2-5);
            padding: var(--space-3) var(--space-3-5);
            border: none;
            border-bottom: 1px solid var(--border-default);
            background: var(--surface-2);
            font-family: inherit;
            cursor: pointer;
            width: 100%;
            text-align: left;
            font-size: var(--text-md);
            flex-shrink: 0;
            transition: background var(--duration-fast);
          }
          .kanban-col-header:hover { background: var(--surface-3); }
          .kanban-col.collapsed .kanban-col-header {
            border-bottom: none;
            border-radius: var(--radius-lg);
          }
          .kanban-col-icon { font-size: var(--text-lg); }
          .kanban-col-title { font-weight: 800; letter-spacing: var(--tracking-tight); }
          .kanban-col-count {
            margin-left: auto;
            background: var(--surface-3);
            border: 1px solid var(--border-subtle);
            border-radius: var(--radius-md);
            padding: var(--space-0-5) var(--space-2-5);
            font-size: var(--text-xs);
            font-weight: 700;
            color: var(--text-tertiary);
            font-feature-settings: var(--tabular-nums);
          }
          .kanban-col-chevron {
            display: inline-block;
            margin-left: 4px;
            transition: transform 0.25s;
            font-size: 12px;
            color: var(--gray-500);
          }
          .kanban-col.collapsed .kanban-col-chevron { transform: rotate(-90deg); }
          .kanban-col.collapsed .kanban-col-body { display: none; }
          .kanban-col-body {
            padding: var(--space-3);
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
            gap: var(--space-3);
            overflow-y: auto;
            flex: 0 0 auto;
            min-height: 0;
            max-height: 75vh;
          }
          .kanban-col-body > * { min-width: 0; }
          .kanban-col-body::-webkit-scrollbar { width: 5px; }
          .kanban-col-body::-webkit-scrollbar-track { background: transparent; }
          .kanban-col-body::-webkit-scrollbar-thumb { background: var(--neutral-300); border-radius: var(--radius-pill); }
          .kanban-col-body::-webkit-scrollbar-thumb:hover { background: var(--neutral-400); }
          .kanban-col-body { scroll-behavior: smooth; }
          .kanban-col-empty {
            text-align: center;
            padding: var(--space-8) var(--space-3-5);
            color: var(--text-disabled);
            font-size: var(--text-sm);
            font-style: italic;
            grid-column: 1 / -1;
          }

          @media (max-width: 900px) {
            .task-table-root { min-height: 0; }
            .kanban-col-body {
              grid-template-columns: 1fr;
              max-height: 65vh;
            }
          }

          /* Responsive Mobile Styles */
          @media (max-width: 768px) {
            .toolbar-row {
              flex-direction: column;
              align-items: stretch;
              gap: 12px;
            }
            .row-1 {
              flex-direction: row !important;
              flex-wrap: wrap !important;
            }
            .search-box-wrapper {
              width: 100% !important;
              flex: 1 1 100% !important;
              min-width: 0 !important;
            }
            .search-suggestions { max-height: 260px; }
            .row-1 .filter-dropdown-wrapper {
              width: calc(50% - 6px) !important;
              flex: none !important;
            }
            
            .row-2 {
              flex-direction: row !important;
              flex-wrap: wrap !important;
            }
            .row-2 .filter-dropdown-wrapper {
              width: calc(50% - 6px) !important;
              flex: none !important;
            }
            .row-2 .filter-dropdown-wrapper:last-child {
              width: 100% !important;
            }

            .date-filter-panel {
              flex-direction: column;
              align-items: stretch;
              gap: 12px;
              padding: 12px;
              width: 100% !important;
            }
            .date-presets-group {
              width: 100%;
            }
            .preset-btn {
              flex: 1;
              justify-content: center;
              padding: 10px 8px;
              font-size: 11.5px;
            }
            .date-divider {
              display: none;
            }
            .custom-date-picker-group {
              width: 100%;
              justify-content: space-between;
              flex-wrap: wrap;
              gap: 6px;
            }
            .clear-date-btn {
              width: 100%;
              justify-content: center;
              margin-top: 4px;
            }
            .task-count-badge {
              margin-left: 0;
              width: 100%;
              min-height: 38px;
            }
            /* Stack the switcher and the export button on their own full-width
               rows, otherwise they compete for space and truncate. */
            .header-actions-group {
              width: 100%;
              margin-left: 0 !important;
              flex-wrap: wrap;
            }
            .header-actions-group .view-switcher-group {
              flex: 1 1 100%;
              width: auto;
            }
            .header-actions-group .switcher-btn {
              flex: 1;
              justify-content: center;
            }
            .export-excel-btn {
              flex: 1 1 100%;
              justify-content: center;
            }
          }
        `}
      </style>
      
      {title && (
        <div className="page-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
          <h1 style={{ margin: 0 }}>{title}</h1>
          <div className="header-actions-group">
            <div className="view-switcher-group">
              {[
                { key: "kanban", icon: "⊡", label: "Kanban" },
                { key: "grid",   icon: "☰", label: "Grid" },
                { key: "gantt",  icon: "📊", label: "Gantt" },
              ].map((v) => (
                <button
                  key={v.key}
                  onClick={() => setView(v.key)}
                  className={`switcher-btn ${view === v.key ? "active" : ""}`}
                >
                  {v.icon} {v.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              className="export-excel-btn"
              onClick={handleExportExcel}
              disabled={exporting || filtered.length === 0}
              title={filtered.length === 0 ? "Nothing to export" : `Download ${filtered.length} task${filtered.length === 1 ? "" : "s"} as Excel`}
            >
              {exporting ? "⏳ Exporting…" : "⬇ Export Excel"}
            </button>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="task-toolbar">
        {/* Row 1: All 5 filters in one line */}
        {/* Row 0: full-width search */}
        <div className="toolbar-row row-search">
          {/* Search + typeahead */}
          <div className="search-box-wrapper" ref={searchBoxRef}>
            <span className="search-icon" aria-hidden="true">🔍</span>
            <input
              type="text"
              className="search-input"
              placeholder="Search tasks, people, company..."
              value={search}
              onChange={e => { setSearch(e.target.value); setSearchOpen(true); setSearchHighlight(-1); }}
              onFocus={() => setSearchOpen(true)}
              onKeyDown={onSearchKeyDown}
              aria-label="Search tasks"
              autoComplete="off"
            />
            {search && (
              <button
                type="button"
                className="search-clear"
                onClick={() => { setSearch(""); setSearchOpen(false); setSearchHighlight(-1); }}
                aria-label="Clear search"
              >×</button>
            )}
            {searchOpen && search.trim() && (
              <div className="search-suggestions" role="listbox">
                {searchSuggestions.length === 0 ? (
                  <div className="search-empty">No matches for “{search.trim()}”</div>
                ) : searchSuggestions.map((sug, i) => (
                  <button
                    key={`${sug.kind}-${sug.value}-${i}`}
                    type="button"
                    role="option"
                    aria-selected={i === searchHighlight}
                    className={`search-sug${i === searchHighlight ? " active" : ""}`}
                    onMouseEnter={() => setSearchHighlight(i)}
                    onClick={() => applySuggestion(sug)}
                  >
                    <span className="sug-icon">
                      {sug.kind === "task" ? "📋" : sug.kind === "person" ? "👤" : "🏢"}
                    </span>
                    <span className="sug-text">{sug.label}</span>
                    {sug.kind === "task" && sug.meta
                      ? <span className="sug-meta">{sug.meta}</span>
                      : <span className="sug-meta">{sug.kind === "person" ? "Employee" : "Company"}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="toolbar-row row-1">
          {/* Status filter dropdown (multi-select) */}
          {!hideStatusFilter && (
            <div className="filter-dropdown-wrapper status-filter">
              <CustomSelect
                placeholder="All Status"
                options={statusOptions}
                value={statusFilter}
                onChange={val => setStatusFilter(Array.isArray(val) ? val : [])}
                multiple={true}
                isClearable={false}
                searchable={false}
              />
            </div>
          )}

          {/* Priority filter dropdown (multi-select) */}
          <div className="filter-dropdown-wrapper priority-filter">
            <CustomSelect
              placeholder="All Priority"
              options={["Low", "Medium", "High"]}
              value={priFilter}
              onChange={val => setPriFilter(Array.isArray(val) ? val : [])}
              multiple={true}
              isClearable={false}
              searchable={false}
            />
          </div>

          {/* Employee filter dropdown (multi-select) */}
          <div className="filter-dropdown-wrapper employee-filter">
            <CustomSelect
              placeholder="All Employees"
              options={employeeOptions}
              value={employeeFilter}
              onChange={val => setEmployeeFilter(Array.isArray(val) ? val : [])}
              multiple={true}
              isClearable={false}
              searchable={true}
            />
          </div>

          {/* Task Creator filter dropdown (multi-select) */}
          <div className="filter-dropdown-wrapper creator-filter">
            <CustomSelect
              placeholder="All Creators"
              options={creatorOptions}
              value={creatorFilter}
              onChange={val => setCreatorFilter(Array.isArray(val) ? val : [])}
              multiple={true}
              isClearable={false}
              searchable={true}
            />
          </div>

          {/* Company filter dropdown */}
          <div className="filter-dropdown-wrapper company-filter">
            <CustomSelect
              placeholder="All Company"
              options={companyOptions}
              value={companyFilter}
              onChange={val => setCompanyFilter(Array.isArray(val) ? val : [])}
              multiple={true}
              isClearable={false}
              searchable={true}
            />
          </div>

          {/* Assignee Department filter dropdown */}
          <div className="filter-dropdown-wrapper assignee-dept-filter">
            <CustomSelect
              placeholder="Assignee Dept"
              options={assigneeDeptOptions}
              value={assigneeDeptFilter}
              onChange={val => setAssigneeDeptFilter(Array.isArray(val) ? val : [])}
              multiple={true}
              isClearable={false}
              searchable={true}
            />
          </div>

          {/* Creator Department filter dropdown */}
          <div className="filter-dropdown-wrapper creator-dept-filter">
            <CustomSelect
              placeholder="Creator Dept"
              options={creatorDeptOptions}
              value={creatorDeptFilter}
              onChange={val => setCreatorDeptFilter(Array.isArray(val) ? val : [])}
              multiple={true}
              isClearable={false}
              searchable={true}
            />
          </div>
        </div>

        {/* Row 3: Total Tasks Count + (when title is inline) View Switcher.
            Date Filter UI removed per request — date-related state stays as
            no-ops so the existing `filtered` logic keeps working. */}
        {/* Bulk assignee removal — only with an employee filter active, and only
            for users who can already delete tasks individually. */}
        {canDelete && employeeFilter.length > 0 && filtered.length > 0 && (
          <div className="toolbar-row" style={{ flexWrap: "wrap" }}>
            <div className="bulk-action-bar">
              <span className="bulk-action-text">
                <strong>{filtered.length}</strong> task{filtered.length === 1 ? "" : "s"} shown for{" "}
                <strong>{employeeFilter.join(", ")}</strong>
              </span>
              <button type="button" className="bulk-action-btn" onClick={() => setBulkOpen(true)}>
                🧹 Remove from these tasks
              </button>
            </div>
          </div>
        )}

        <div className="toolbar-row row-3" style={{ flexWrap: "wrap" }}>
          <div className="task-count-badge" style={{ marginLeft: 0 }}>
            Total Tasks: {filtered.length} / {records?.length || 0}
          </div>

          {!title && (
            <div className="header-actions-group" style={{ marginLeft: "auto" }}>
              <div className="view-switcher-group">
                {[
                  { key: "kanban", icon: "⊡", label: "Kanban" },
                  { key: "grid",   icon: "☰", label: "Grid" },
                  { key: "gantt",  icon: "📊", label: "Gantt" },
                ].map((v) => (
                  <button
                    key={v.key}
                    onClick={() => setView(v.key)}
                    className={`switcher-btn ${view === v.key ? "active" : ""}`}
                  >
                    {v.icon} {v.label}
                  </button>
                ))}
              </div>
              <button
                type="button"
                className="export-excel-btn"
                onClick={handleExportExcel}
                disabled={exporting || filtered.length === 0}
                title={filtered.length === 0 ? "Nothing to export" : `Download ${filtered.length} task${filtered.length === 1 ? "" : "s"} as Excel`}
              >
                {exporting ? "⏳ Exporting…" : "⬇ Export Excel"}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Empty state — shown when there are no tasks at all (any view).
          Kanban hides individual empty columns; this fires only when every
          column would be empty. */}
      {filtered.length === 0 && (
        <div style={{ textAlign: "center", padding: "60px 20px", color: "var(--gray-400)" }}>
          <div style={{ fontSize: 48, marginBottom: 14, opacity: 0.5 }}>📭</div>
          <p style={{ fontSize: 14, fontWeight: 600 }}>{search ? "No tasks match your search." : emptyMsg}</p>
        </div>
      )}

      {/* ── KANBAN VIEW ─────────────────────────────────────── */}
      {view === "kanban" && filtered.length > 0 && (() => {
        const allCols = (
          kanbanLayout === "5col"
            ? [
                { key: "overdue",   label: "Overdue",   icon: "🔴", color: "var(--danger)",  bg: "var(--danger-muted)" },
                { key: "today",     label: "Today",     icon: "🔥", color: "var(--danger)",  bg: "var(--surface-0)" },
                { key: "tomorrow",  label: "Tomorrow",  icon: "⏰", color: "var(--warning)", bg: "var(--warning-muted)" },
                { key: "upcoming",  label: "Upcoming",  icon: "🗓️", color: "var(--info)",    bg: "var(--info-muted)" },
                { key: "completed", label: "Completed", icon: "✅", color: "var(--success)", bg: "var(--success-muted)" },
              ]
            : kanbanLayout === "completedDate"
              ? [
                  { key: "today",     label: "Today",     icon: "✅", color: "var(--success)", bg: "var(--success-muted)" },
                  { key: "yesterday", label: "Yesterday", icon: "📅", color: "var(--warning)", bg: "var(--warning-muted)" },
                  { key: "previous",  label: "Previous",  icon: "🗓️", color: "var(--text-tertiary)", bg: "var(--surface-3)" },
                ]
            : kanbanLayout === "overdueDate"
              ? [
                  { key: "today",     label: "Today",     icon: "🔴", color: "var(--danger)",  bg: "var(--surface-0)" },
                  { key: "yesterday", label: "Yesterday", icon: "⏰", color: "var(--warning)", bg: "var(--warning-muted)" },
                  { key: "previous",  label: "Previous",  icon: "🗓️", color: "var(--text-tertiary)", bg: "var(--surface-3)" },
                ]
            : kanbanLayout === "recurringType"
              ? [
                  { key: "daily",      label: "Daily",       icon: "🔄", color: "var(--success)", bg: "var(--success-muted)" },
                  { key: "weekly",     label: "Weekly",      icon: "🔄", color: "var(--purple)",  bg: "var(--purple-muted)" },
                  { key: "monthly",    label: "Monthly",     icon: "🔄", color: "#EC4899",        bg: "rgba(236,72,153,0.12)" },
                  { key: "quarterly",  label: "Quarterly",   icon: "🔄", color: "var(--primary)", bg: "var(--primary-light)" },
                  { key: "halfYearly", label: "Half Yearly", icon: "🔄", color: "var(--warning)", bg: "var(--warning-muted)" },
                  { key: "yearly",     label: "Yearly",      icon: "🔄", color: "var(--danger)",  bg: "var(--surface-0)" },
                  { key: "other",      label: "Other",       icon: "🔄", color: "var(--text-tertiary)", bg: "var(--surface-3)" },
                ]
            : kanbanLayout === "createdTask9col"
              ? [
                  { key: "today",      label: "Today",       icon: "🆕", color: "var(--info)",    bg: "var(--info-muted)" },
                  { key: "yesterday",  label: "Yesterday",   icon: "📅", color: "var(--warning)", bg: "var(--warning-muted)" },
                  { key: "previous",   label: "Previous",    icon: "🗓️", color: "var(--text-tertiary)", bg: "var(--surface-3)" },
                  { key: "daily",      label: "Daily",       icon: "🔄", color: "var(--success)", bg: "var(--success-muted)" },
                  { key: "weekly",     label: "Weekly",      icon: "🔄", color: "var(--purple)",  bg: "var(--purple-muted)" },
                  { key: "monthly",    label: "Monthly",     icon: "🔄", color: "#EC4899",        bg: "rgba(236,72,153,0.12)" },
                  { key: "quarterly",  label: "Quarterly",   icon: "🔄", color: "var(--primary)", bg: "var(--primary-light)" },
                  { key: "halfYearly", label: "Half Yearly", icon: "🔄", color: "var(--warning)", bg: "var(--warning-muted)" },
                  { key: "yearly",     label: "Yearly",      icon: "🔄", color: "var(--danger)",  bg: "var(--surface-0)" },
                ]
              : [
                  { key: "today",    label: "Today",    icon: "🔥", color: "var(--danger)",  bg: "var(--surface-0)" },
                  { key: "tomorrow", label: "Tomorrow", icon: "⏰", color: "var(--warning)", bg: "var(--warning-muted)" },
                  { key: "upcoming", label: "Upcoming", icon: "🗓️", color: "var(--info)",    bg: "var(--info-muted)" },
                ]
        );
        // Drop empty buckets so empty columns don't render.
        const visibleCols = allCols.filter(col => ((kanbanBuckets[col.key] || []).length > 0));
        // The wrap's data-cols attribute drives the dynamic-width CSS:
        //   1 col → ~2/3 width, 2 cols → ~1/2 each, 3+ cols → ~1/3 each.
        return (
          <div className={`kanban-three-col-wrap layout-${kanbanLayout}`} data-cols={visibleCols.length}>
            {visibleCols.map(col => {
              const items = kanbanBuckets[col.key] || [];
              const isExpanded = colExpanded[col.key];
              return (
                <div key={col.key} className={`kanban-col ${isExpanded ? "expanded" : "collapsed"}`}>
                  <button
                    type="button"
                    className="kanban-col-header"
                    onClick={() => toggleCol(col.key)}
                    style={{ background: col.bg }}
                  >
                    <span className="kanban-col-icon">{col.icon}</span>
                    <span className="kanban-col-title" style={{ color: col.color }}>{col.label}</span>
                    <span className="kanban-col-count">{items.length}</span>
                    <span className="kanban-col-chevron">▾</span>
                  </button>
                  <div className="kanban-col-body">
                    {items.length === 0 ? (
                      <div className="kanban-col-empty">No tasks</div>
                    ) : (<>
                      {items.slice(0, getColLimit(col.key)).map((row, i) => renderKanbanCard(row, i))}
                      {items.length > getColLimit(col.key) && (
                        <ScrollSentinel onVisible={() => showMoreCol(col.key)} />
                      )}
                    </>)}
                  </div>
                </div>
              );
            })}
            {commentTask && (
              <CommentPanel
                taskId={cellText(commentTask.fields?.["Task ID"]) || commentTask.record_id}
                taskName={cellText(commentTask.fields?.["Task Name"]) || "Task"}
                priority={cellText(commentTask.fields?.["Priority"]) || "Medium"}
                onClose={() => setCommentTask(null)}
              />
            )}
          </div>
        );
      })()}

      {/* ── GRID VIEW ────────────────────────────────────── */}
      {filtered.length > 0 && view === "grid" && (
        <div style={{ overflowX: "auto", borderRadius: 12, border: "1px solid var(--border-default)", boxShadow: "var(--shadow-sm)" }}>
          <table className="task-grid-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, fontFamily: "inherit" }}>
            <thead>
              <tr>
                {["Task ID","Task Name","Status","Priority","Assigned To","Supervisor","Start Date","Due Date","Company","Department","Recurring","Actions"].map(h => (
                  <th key={h}
                      onClick={() => {
                        if (h === "Actions") return;
                        if (sortCol === h) {
                          setSortAsc(!sortAsc);
                        } else {
                          setSortCol(h);
                          setSortAsc(true);
                        }
                      }}
                      style={{ padding: "11px 14px", textAlign: "left", fontWeight: 700, fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase", whiteSpace: "nowrap", position: "sticky", top: 0, background: "var(--surface-2)", color: "var(--text-secondary)", zIndex: 1, borderBottom: "1px solid var(--border-default)", cursor: h === "Actions" ? "default" : "pointer", userSelect: "none" }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      {h}
                      {sortCol === h && (
                        <span style={{ fontSize: 10, opacity: 0.8 }}>{sortAsc ? "▲" : "▼"}</span>
                      )}
                      {sortCol !== h && h !== "Actions" && (
                        <span style={{ fontSize: 10, opacity: 0.3 }}>↕</span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, gridLimit).map((row, i) => {
                const f = row.fields || {};
                const rid = row.record_id || i;
                const statusText = cellText(f["Overall Status"]) || "Not Started";
                const isDone = statusText.toLowerCase().includes("done") || statusText.includes("✅");
                const isUserDone = !!f["My Completion"];
                const priority = cellText(f["Priority"]) || "Medium";
                const isOverdue = statusText.toLowerCase().includes("overdue");
                const effectivelyDone = tableId === USER_TABLE ? isUserDone : isDone;
                const completedAtRaw = f["Completed At"] || f["Completion Time"];
                const completedTime = normalizeToMs(completedAtRaw);
                const dueTime = normalizeToMs(f["End Date"]);
                const isLate = effectivelyDone && completedTime && dueTime && (completedTime > (dueTime + 60000));
                const s = sc(statusText);
                const pb = getPriorityBadge(priority);
                const assignedRaw = f["Total Assigned Users"] || f["All Assigned Users"] || f["Assigned Users"] || f["User"];
                let assignedStr = "—";
                if (Array.isArray(assignedRaw)) {
                  const names = assignedRaw.map(v => v.en_name || v.name || null).filter(Boolean);
                  if (names.length) assignedStr = names.join(", ");
                } else { assignedStr = cellText(assignedRaw) || "—"; }
                const gridTid = cellText(f["Task ID"]);
                const gridCreatorField = (gridTid && adminCreatorMap[gridTid]) || f["Task Creator"] || f["Assigned By"];
                const gridIsCreator = userMatchesField(gridCreatorField, user);
                const completing = false;
                return (
                  <tr key={rid} style={{
                    background: isLate ? "var(--danger-muted)" : i % 2 === 0 ? "var(--surface-0)" : "var(--surface-1)",
                    borderBottom: "1px solid var(--border-subtle)",
                    borderLeft: isLate ? "3px solid var(--danger)" : isOverdue ? "3px solid var(--warning)" : "3px solid transparent",
                    transition: "background 0.15s",
                  }}
                    onMouseEnter={e => e.currentTarget.style.background = "var(--surface-2)"}
                    onMouseLeave={e => e.currentTarget.style.background = isLate ? "var(--danger-muted)" : i % 2 === 0 ? "var(--surface-0)" : "var(--surface-1)"}
                  >
                    <td style={{ padding: "10px 14px", fontWeight: 700, color: "var(--primary)", whiteSpace: "nowrap", fontSize: 12 }}>{cellText(f["Task ID"]) || "—"}</td>
                    <td style={{ padding: "10px 14px", fontWeight: 600, whiteSpace: "nowrap", textDecoration: effectivelyDone ? "line-through" : "none", color: effectivelyDone ? "var(--text-disabled)" : "var(--text-primary)" }}>{cellText(f["Task Name"]) || "Untitled"}</td>
                    <td style={{ padding: "10px 14px", whiteSpace: "nowrap" }}>
                      <span style={{ padding: "3px 9px", borderRadius: 20, fontSize: 10, fontWeight: 700, background: isLate ? "var(--danger-muted)" : s.bg, color: isLate ? "var(--danger)" : s.color }}>
                        {isLate ? "🔴 Overdue (Done)" : statusText}
                      </span>
                    </td>
                    <td style={{ padding: "10px 14px", whiteSpace: "nowrap" }}>
                      <span style={{ padding: "3px 9px", borderRadius: 20, fontSize: 10, fontWeight: 700, background: pb.bg, color: pb.color }}>{pb.label}</span>
                    </td>
                    <td style={{ padding: "10px 14px", color: "var(--text-secondary)", whiteSpace: "nowrap" }}>{assignedStr}</td>
                    <td style={{ padding: "10px 14px", color: "var(--text-secondary)", whiteSpace: "nowrap" }}>{cellText(f["Supervisor"]) || "—"}</td>
                    <td style={{ padding: "10px 14px", color: "var(--text-tertiary)", whiteSpace: "nowrap", fontSize: 12 }}>{formatDateTime(f["Start Date"])}</td>
                    <td style={{ padding: "10px 14px", whiteSpace: "nowrap", fontSize: 12, fontWeight: isOverdue ? 700 : 400, color: isOverdue ? "var(--danger)" : "var(--text-tertiary)" }}>{formatDateTime(f["End Date"])}</td>
                    <td style={{ padding: "10px 14px", color: "var(--text-tertiary)", whiteSpace: "nowrap", fontSize: 12 }}>{cellText(f["Company Name"]) || "—"}</td>
                    <td style={{ padding: "10px 14px", color: "var(--text-tertiary)", whiteSpace: "nowrap", fontSize: 12 }}>{cellText(f["Assignee Department"]) || "—"}</td>
                    <td style={{ padding: "10px 14px", whiteSpace: "nowrap" }}>
                      {(() => { const rc = cellText(f["Recurring Type"]); return rc && rc !== "—" && rc !== "Not Applicable" ? <span style={{ fontSize: 11, color: "var(--purple)", background: "var(--purple-light)", padding: "2px 8px", borderRadius: 10 }}>🔄 {rc}</span> : <span style={{ color: "var(--text-disabled)", fontSize: 12 }}>—</span>; })()}
                    </td>
                    <td style={{ padding: "10px 14px", whiteSpace: "nowrap" }}>
                      <div style={{ display: "flex", gap: 5 }}>
                        <button onClick={() => setCommentTask(row)}
                          className={(() => {
                            const tid = cellText(f["Task ID"]);
                            return flashTaskId && tid && String(flashTaskId) === String(tid) ? "comment-btn-flash" : "";
                          })()}
                          style={{ padding: "5px 9px", borderRadius: 7, border: "1px solid var(--border-default)", background: "var(--surface-1)", color: "var(--purple)", fontSize: 12, cursor: "pointer", fontFamily: "inherit", fontWeight: 600, position: "relative", transition: "var(--transition-fast)", boxShadow: "var(--shadow-xs)" }}>
                          💬
                          {(() => {
                            const gridTaskId = cellText(row.fields?.["Task ID"]) || row.record_id;
                            const total = commentCounts?.[gridTaskId] || 0;
                            const read = readComments?.[gridTaskId] || 0;
                            const unread = Math.max(0, total - read);
                            return unread > 0 && (
                              <span style={{
                                position: "absolute", top: -5, right: -5,
                                background: "var(--danger)", color: "white",
                                fontSize: 9, fontWeight: 800, padding: "2px 5px",
                                borderRadius: 10, boxShadow: "var(--shadow-glow-danger)"
                              }}>
                                {unread}
                              </span>
                            );
                          })()}
                        </button>
                        {canComplete && (() => {
                          const gridUserDoneButPending = tableId === USER_TABLE && isUserDone && !isDone;
                          const gridShowReopen = tableId === USER_TABLE ? (isUserDone && isDone) : isDone;
                          if (gridUserDoneButPending) {
                            return <span style={{ padding: "5px 9px", borderRadius: 7, border: "1px solid var(--success-border, var(--border-default))", background: "var(--success-muted)", color: "var(--success)", fontSize: 12, fontFamily: "inherit", fontWeight: 600 }} title="You've completed — waiting for others">✅</span>;
                          }
                          return <button onClick={() => handleComplete(row)} style={{ padding: "5px 9px", borderRadius: 7, border: "1px solid var(--border-default)", background: gridShowReopen ? "var(--warning-muted)" : "var(--surface-1)", color: gridShowReopen ? "var(--warning)" : "var(--primary)", fontSize: 12, cursor: "pointer", fontFamily: "inherit", fontWeight: 600, transition: "var(--transition-fast)", boxShadow: "var(--shadow-xs)" }} title={gridShowReopen ? "Reopen Task" : "Mark as Done"}>{gridShowReopen ? "↺" : "✓"}</button>;
                        })()}
                        {(canEdit || gridIsCreator) && (
                          <button onClick={() => { const gridAdminRec = gridTid && adminRecordByTaskId[gridTid]; setEditRecord(gridAdminRec || row); }} style={{ padding: "5px 9px", borderRadius: 7, border: "1px solid var(--border-default)", background: "var(--surface-1)", color: "var(--primary)", fontSize: 12, cursor: "pointer", fontFamily: "inherit", fontWeight: 600, transition: "var(--transition-fast)", boxShadow: "var(--shadow-xs)" }}>✏️</button>
                        )}
                        {(canDelete || gridIsCreator) && (
                          <button onClick={() => { const gridAdminRec = gridTid && adminRecordByTaskId[gridTid]; setDeleteRecord(gridAdminRec || row); }} style={{ padding: "5px 9px", borderRadius: 7, border: "1px solid var(--danger-border)", background: "var(--danger-muted)", color: "var(--danger)", fontSize: 12, cursor: "pointer", fontFamily: "inherit", fontWeight: 600, transition: "var(--transition-fast)", boxShadow: "var(--shadow-xs)" }}>🗑️</button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filtered.length > gridLimit && (
            <ScrollSentinel onVisible={() => setGridLimit(prev => prev + 100)} />
          )}
          {commentTask && (
            <CommentPanel
              taskId={cellText(commentTask.fields?.["Task ID"]) || commentTask.record_id}
              taskName={cellText(commentTask.fields?.["Task Name"]) || "Task"}
              priority={cellText(commentTask.fields?.["Priority"]) || "Medium"}
              onClose={() => setCommentTask(null)}
            />
          )}
        </div>
      )}

      {/* ── GANTT VIEW ────────────────────────────────────── */}
      {filtered.length > 0 && view === "gantt" && (() => {
        const ROW_H = 44;
        const LEFT_W = 220;
        const DAY_W = 36;
        const totalDays = Math.ceil(ganttRange.totalMs / 86400000);
        const days = Array.from({ length: totalDays }, (_, i) => new Date(ganttRange.start + i * 86400000));
        const today = Date.now();
        const todayOffsetDays = (today - ganttRange.start) / 86400000;
        const todayOffsetPx = todayOffsetDays * DAY_W;
        const barColor = (st, late) => {
          if (late) return "var(--danger)";
          const s = (st || "").toLowerCase();
          if (s.includes("overdue")) return "var(--warning)";
          if (s.includes("done") || s.includes("complet")) return "var(--success)";
          if (s.includes("progress")) return "#F59E0B";
          return "var(--primary)";
        };
        return (
          <div style={{ borderRadius: 12, border: "1px solid var(--border-default)", overflow: "hidden", boxShadow: "var(--shadow-sm)" }}>
            <div style={{ display: "flex" }}>
              {/* Left panel — task list */}
              <div style={{ width: LEFT_W, minWidth: LEFT_W, flexShrink: 0, borderRight: "1px solid var(--border-default)", zIndex: 2 }}>
                <div style={{ height: ROW_H, background: "var(--surface-2)", display: "flex", alignItems: "center", padding: "0 14px", fontWeight: 700, fontSize: 12, color: "var(--text-secondary)", letterSpacing: "0.04em", textTransform: "uppercase" }}>TASK</div>
                {filtered.map((row, i) => {
                  const f = row.fields || {};
                  const rid = row.record_id || i;
                  const statusText = cellText(f["Overall Status"]) || "Not Started";
                  const effectivelyDone = tableId === USER_TABLE ? !!f["My Completion"] : (statusText.toLowerCase().includes("done") || statusText.includes("✅"));
                  return (
                    <div key={rid} style={{
                      height: ROW_H, display: "flex", flexDirection: "column", justifyContent: "center",
                      padding: "0 14px", borderBottom: "1px solid var(--border-subtle)",
                      background: i % 2 === 0 ? "var(--surface-0)" : "var(--surface-1)",
                    }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: "var(--primary)", lineHeight: 1 }}>{cellText(f["Task ID"]) || `#${i + 1}`}</div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: effectivelyDone ? "var(--text-disabled)" : "var(--text-primary)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", textDecoration: effectivelyDone ? "line-through" : "none", lineHeight: 1.3, marginTop: 2 }}>
                        {cellText(f["Task Name"]) || "Untitled"}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Right panel — timeline */}
              <div style={{ flex: 1, overflowX: "auto", position: "relative" }}>
                {/* Day header */}
                <div style={{ display: "flex", width: totalDays * DAY_W, height: ROW_H, background: "var(--surface-2)", position: "sticky", top: 0, zIndex: 1 }}>
                  {days.map((d, idx) => {
                    const isToday = d.toDateString() === new Date().toDateString();
                    const showMonth = idx === 0 || d.getDate() === 1;
                    return (
                      <div key={idx} style={{
                        minWidth: DAY_W, width: DAY_W, textAlign: "center", fontSize: 10, color: isToday ? "var(--primary)" : "var(--text-tertiary)",
                        fontWeight: isToday ? 800 : showMonth ? 700 : 400, display: "flex", flexDirection: "column",
                        alignItems: "center", justifyContent: "center", borderRight: "1px solid var(--border-subtle)",
                        background: isToday ? "var(--primary-light)" : "transparent",
                      }}>
                        {showMonth && <span style={{ fontSize: 8, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.06em", opacity: 0.9 }}>{d.toLocaleDateString("en", { month: "short" })}</span>}
                        <span>{d.getDate()}</span>
                      </div>
                    );
                  })}
                </div>

                {/* Task bars */}
                {filtered.map((row, i) => {
                  const f = row.fields || {};
                  const rid = row.record_id || i;
                  const statusText = cellText(f["Overall Status"]) || "Not Started";
                  const effectivelyDone = tableId === USER_TABLE ? !!f["My Completion"] : (statusText.toLowerCase().includes("done") || statusText.includes("✅"));
                  const completedAtRaw = f["Completed At"] || f["Completion Time"];
                  const completedTime = normalizeToMs(completedAtRaw);
                  const dueTime = normalizeToMs(f["End Date"]);
                  const isLate = effectivelyDone && completedTime && dueTime && (completedTime > (dueTime + 60000));
                  let startMs = normalizeToMs(f["Start Date"]);
                  let endMs = normalizeToMs(f["End Date"]);
                  if (!startMs || startMs <= 0) startMs = today;
                  if (!endMs || endMs <= 0) endMs = startMs + 86400000;
                  if (endMs < startMs) endMs = startMs + 86400000;
                  
                  const offsetDays = Math.max(0, (startMs - ganttRange.start) / 86400000);
                  const durationDays = Math.max(0.1, (endMs - startMs) / 86400000);
                  const leftPx = offsetDays * DAY_W;
                  const widthPx = durationDays * DAY_W;
                  
                  const color = barColor(statusText, isLate);
                  const taskLabel = cellText(f["Task Name"]) || "Untitled";
                  return (
                    <div key={rid} style={{
                      height: ROW_H, position: "relative", borderBottom: "1px solid var(--border-subtle)",
                      background: i % 2 === 0 ? "var(--surface-0)" : "var(--surface-1)",
                      display: "flex", alignItems: "center",
                      width: totalDays * DAY_W,
                    }}>
                      {/* Day grid lines */}
                      {days.map((d, di) => (
                        <div key={di} style={{ position: "absolute", left: di * DAY_W, top: 0, bottom: 0, width: 1, background: d.toDateString() === new Date().toDateString() ? "var(--primary-border)" : "var(--border-subtle)" }} />
                      ))}
                      {/* Task bar */}
                      <div style={{
                        position: "absolute",
                        left: `${leftPx + 2}px`,
                        width: `${Math.max(10, widthPx - 4)}px`,
                        height: 26, borderRadius: 6,
                        background: color,
                        opacity: effectivelyDone ? 0.7 : 1,
                        display: "flex", alignItems: "center", paddingLeft: 8, overflow: "hidden",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
                        cursor: "default",
                        transition: "opacity 0.15s",
                      }} title={`${taskLabel}\n${formatDate(f["Start Date"])} → ${formatDate(f["End Date"])}`}>
                        <span style={{ color: "white", fontSize: 11, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{taskLabel}</span>
                      </div>
                      {/* Today line */}
                      {todayOffsetDays >= 0 && todayOffsetDays <= totalDays && (
                        <div style={{ position: "absolute", left: `${todayOffsetPx}px`, top: 0, bottom: 0, width: 2, background: "var(--primary)", opacity: 0.8, zIndex: 2, pointerEvents: "none" }} />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {commentTask && (
              <CommentPanel
                taskId={cellText(commentTask.fields?.["Task ID"]) || commentTask.record_id}
                taskName={cellText(commentTask.fields?.["Task Name"]) || "Task"}
                priority={cellText(commentTask.fields?.["Priority"]) || "Medium"}
                onClose={() => setCommentTask(null)}
              />
            )}
          </div>
        );
      })()}

      {editRecord && <EditTaskModal record={editRecord} tableId={ADMIN_TABLE} onClose={() => setEditRecord(null)} />}
      {bulkOpen && (
        <BulkAssigneeRemoveModal
          records={filtered}
          tableId={tableId}
          selectedNames={employeeFilter}
          onClose={() => setBulkOpen(false)}
        />
      )}

      {deleteRecord && <DeleteConfirmModal record={deleteRecord} tableId={ADMIN_TABLE} onClose={() => setDeleteRecord(null)} />}

      {/* Success toast */}
      {toastMsg && (
        <div style={{
          position: "fixed", bottom: 28, left: "50%", transform: "translateX(-50%)",
          background: "var(--success)", color: "#fff", padding: "12px 28px",
          borderRadius: "var(--radius-lg)", fontSize: 14, fontWeight: 600,
          boxShadow: "0 4px 24px rgba(16,185,129,0.3)", zIndex: 99999,
          display: "flex", alignItems: "center", gap: 8,
          animation: "toastSlideUp 0.35s cubic-bezier(0.4,0,0.2,1)",
        }}>
          <span>✅</span> {toastMsg}
        </div>
      )}
    </div>
  );
}
